import { clone, cloneDeep, isEqual, union, uniqueId } from "lodash";
import moment from "moment";
import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { GeographyService } from "@app/geography";
import { Message, MessageType, NotificationContextProps, withNotificationContext } from "@app/notifications";
import { AccuracyGapAnalysisStatusChangeNotificationMessagePayload, BulkTargetProjectRunStatusChangeNotificationMessagePayload, MultiProjectRunStatusChangePayload, ProjectRunStatusChangePayload, PciDashboardStatusChangeNotificationMessagePayload } from "@app/notifications/message";
import {
  createRunConfigurationResult, Project, CountyProjectContext, ProjectPerspective,
  ProjectRunConfigurationResult, ProjectService, RegionFilterItem,
} from "@app/projects";
import { AccuracyTrendChartItem, AdequacyTrendChartItem, AnalysisSpecialtyGroup, ResultRegion, SpecialtyGroupException, SpecialtyGroupExceptionStatus, TrendChartItemBase } from "@app/projects/results/common";
import { RegionGranularity, RegionSetQualifier } from "@app/region-sets";
import { RouteQueryProps, withQuery } from "@app/shared/query";
import { AccuracyTemplate, AccuracyTemplateProvider, TemplateMetadata, TemplateService } from "@app/templates";
import { Roles, UserContextProps, withUserContext } from "@app/user";
import { AccuracyTemplateInspection } from "../templates/accuracy/models";
import { TrendChartItemType } from "../velvet/enumerators";
import { ChangedGapsEnum, FilterType, NetworkAssessmentAnalysisType, ProjectRunConfigurationStatus, ProjectRunConfigurationType, RegionSetStatusType, TimeFrequency, AttestationRateDays, SpecialtyTypeEnum } from "./enumerators";
import { CountyProjectContainerState, CountyProjectContextProps } from "./index";
import { ProjectModule, SpecialtyGroupEntityType } from "./literals";
import { AccuracyAggregate, AccuracyDetail, AccuracyFilters, AccuracyResult, AccuracyService } from "./network-assessment/accuracy";
import { AdequacyAggregate, AdequacyDetail, AdequacyFilters, AdequacyResult } from "./network-assessment/adequacy";
import { AttestationAggregate, AttestationDetail, AttestationResult, AttestationService } from "./network-assessment/attestation";
import { CompositeAggregate, CompositeDetail, CompositeFilters, CompositeResult } from "./network-assessment/composite";
import { RegionQueryShape } from "./network-assessment/RegionStatusFilter";
import StateName from "./network-assessment/stateName";
import ProjectRunConfiguration from "./projectRunConfiguration";
import RegionFilters from "./regionFilters";
import NextClosestProviderService from "./servicing-provider/nextClosestProviderService";
import AccuracyGapAnalysisService from "./accuracy-gap-analysis/AccuracyGapAnalysisService";
import SpecialtyGroupExceptionsService from "@app/projects/specialtyGroupExceptionsService";
import { getLocalStorageRunConfigurationKey, removeLocalStorageRunConfigurationById } from "../velvet/helpers";
import { LocalStorageRunConfigurationItem } from "../velvet/types";
import { convertDateTime } from "@app/shared/convertDateTime";
import { querystringify, parseQuerystring } from "@app/shared/utility";
import { bandDisplayNames } from "./servicing-provider/accuracy/provider-inspection-profile-components/utils";
import { CleanHistoryProps, withCleanHistory } from "@app/shared/useCleanHistory";
import { Features } from "@app/feature-flags";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { datadogRum } from "@datadog/browser-rum";

interface CountyProjectContainerProps {
  children?: ((value: CountyProjectContextProps) => React.ReactNode) | React.ReactNode;
  projectId: number;
}

interface RunConfigurationTypeProps {
  projectRunConfigurationType?: ProjectRunConfigurationType;
}

/** The shape of possible query string parameters. */
interface AdequacyQueryShape {
  all?: string;
  changedGaps?: string;
  exceptions?: string;
  gaps?: string;
  specialtyType?: string;
  viewBy?: ProjectPerspective;
}

interface CountyProjectContainerLocationState {
  compareToRunConfiguration?: ProjectRunConfigurationResult;
  initialDualMembershipMode?: boolean;
  isDualMembershipMode?: boolean;
  runConfiguration?: ProjectRunConfigurationResult;
}

type CountyProjectContainerCombinedProps =
  FeatureFlagContextProps &
  CountyProjectContainerProps &
  NotificationContextProps &
  UserContextProps &
  CleanHistoryProps &
  RouteComponentProps<unknown, unknown, CountyProjectContainerLocationState> &
  RouteQueryProps<AdequacyFilters> &
  RouteQueryProps<RunConfigurationTypeProps>;

const defaultAdequacyResult: AdequacyResult = {
  adequacyResultTypeText: undefined,
  aggregateDetails: [],
  aggregateSummary: null,
  availableRegionSetStatusTypes: [],
  details: [],
};

const defaultAccuracyResult: AccuracyResult = {
  aggregateDetails: [],
  aggregateSummary: null,
  availableRegionSetStatusTypes: [],
  details: [],
  resultTypeText: undefined,
};

const defaultAttestationResult: AttestationResult = {
  aggregateDetails: [],
  aggregateSummary: null,
  details: [],
  resultTypeText: undefined,
};

const defaultCompositeResult: CompositeResult = {
  aggregateDetails: [],
  aggregateSummary: null,
  details: [],
  resultTypeText: undefined,
};

/** Acts as a container for information surrounding project defined by the provided props. */
class CountyProjectContainer extends React.Component<CountyProjectContainerCombinedProps, CountyProjectContainerState> {
  private isCancelled = false;
  private statesMap: { [key: string]: string } = {};
  private readonly geographyService = new GeographyService();
  private readonly accuracyService = new AccuracyService();
  private readonly attestationService = new AttestationService();
  private readonly projectService = new ProjectService();
  private readonly specialtyGroupExceptionsService = new SpecialtyGroupExceptionsService();
  private readonly nextClosestProviderService = new NextClosestProviderService();
  private readonly accuracyGapAnalysisService = new AccuracyGapAnalysisService();
  private readonly templateService = new TemplateService();

  private runConfigurationMap: { [runConfigurationIdNetworkAssessmentAnalysisTypeKey: string]: ProjectRunConfigurationResult } = {};
  /* Used to track the currently selected runConfigurationId. In order to provide a good user experience,
   * since this value is no longer available in the query, we want to try keep this value populated with
   * the most recent run configuration id selected by the user or by the system. This way, when we need
   * to clear out the actual runConfiguration object, we can then rebuild the correct run configuration object.
   * Otherwise, we'll have to always jumping to the most recent runConfigurationId which would force the user
   * to navigate back to their previous selection */
  private runConfigurationId?: number = undefined;

  /* The currentLoadingTrackerId is used to make sure the correct
    results are rendered on the page. If a filter is added or removed while the
    results are still being awaited, then
    there could be a case where the results finish and render,
    then the prior results finish and re-render the page with the wrong results.
    If multiple concurrent calls are made to updateResults, we want to only show
    the results from the last call, and ignore the rest. */
  private currentLoadingTrackerId = "";

  constructor(props: CountyProjectContainerCombinedProps) {
    super(props);
    const initialPerspective = (parseQuerystring(this.props.location.search) as AdequacyQueryShape).viewBy as ProjectPerspective || ProjectPerspective.ByState;
    const initialModule = this.module(this.props.location.pathname);

    this.state = {
      accuracyFilters: { ...this.props.query },
      accuracyResult: { aggregateDetails: [], aggregateSummary: null, availableRegionSetStatusTypes: [], details: [] },
      accuracyTemplate: null,
      accuracyTrendChartData: [],
      adequacyFilters: { ...this.props.query },
      adequacyResult: { aggregateDetails: [], aggregateSummary: null, availableRegionSetStatusTypes: [], details: [] },
      adequacyTrendChartData: [],
      adequacyTrendChartDataDualMembership: [],
      attestationRateDays: AttestationRateDays.ThreeSixtyFive,
      attestationResult: { aggregateDetails: [], aggregateSummary: null, details: [] },
      availablePerspectives: this.getAvailablePerspectives(initialModule, {}),
      clearCachedImpactAnalysisRunConfigurations: this.clearCachedImpactAnalysisRunConfigurations,
      clearFilters: this.clearFilters,
      cmsCreditIsApplying: false,
      cmsCreditShowOnlyChanges: false,
      cmsCreditTemplateValue: "10", // this will come from a template eventually
      compareToRunConfiguration: (this.props.location.state && this.props.location.state.compareToRunConfiguration) || null,
      compareToRunConfigurationTimestamp: null,
      compositeFilters: { ...this.props.query },
      compositeResult: { aggregateDetails: [], aggregateSummary: null, details: [] },
      countyRegionSetIds: [],
      error: null,
      filters: [],
      forceUpdateProject: this.forceUpdateProject,
      hasProspectOrMarketProviders: true, // assume true because NCIP panels and viewable fielsd render if false
      initialDualMembershipMode: this.props.location.state && this.props.location.state.initialDualMembershipMode,
      isDualMembershipMode: false,
      loadedAccuracyTemplates: false,
      loadedDefaultPerspective: false,
      loadedFilters: false,
      loading: true,
      loadingResults: true,
      module: initialModule,
      networkAssessmentAnalysisType: NetworkAssessmentAnalysisType.ServiceArea,
      perspective: initialPerspective,
      processProviderChanges: this.processProviderChanges,
      project: null,
      regionFilters: {},
      removeFilter: this.removeFilter,
      requeueMarketAndProspectNCP: this.requeueMarketAndProspectNCP,
      rerunAccuracyGapAnalysis: this.rerunAccuracyGapAnalysis,
      runConfiguration: (this.props.location.state?.runConfiguration as ProjectRunConfigurationResult) || null,
      runConfigurationTimestamp: null,
      runFrequency: TimeFrequency.All,
      runProject: this.runProject,
      setAttestationRateDays: this.setAttestationRateDays,
      setBulkStatus: this.setBulkStatus,
      setCMSCreditIsApplying: this.setCMSCreditIsApplying,
      setCMSCreditShowOnlyChanges: this.setCMSCreditShowOnlyChanges,
      setDualMembershipMode: this.setDualMembershipMode,
      setException: this.setException,
      setLoading: this.setLoading,
      setPerspective: this.setPerspective,
      setProjectHasUnprocessedProjectionProviders: this.setProjectHasUnprocessedProjectionProviders,
      setProjectHasUnprocessedTerminations: this.setProjectHasUnprocessedTerminations,
      setProjectProjectionFlags: this.setProjectProjectionFlags,
      setProjectReadOnlyProperties: this.setProjectReadOnlyProperties,
      setRegionFilters: this.setRegionFilters,
      setRunConfiguration: this.setRunConfiguration,
      setShouldRefreshMarketProviders: this.setShouldRefreshMarketProviders,
      setShouldRefreshNetNewProviders: this.setShouldRefreshNetNewProviders,
      setShouldRefreshNetworkProviders: this.setShouldRefreshNetworkProviders,
      setShouldRefreshProspectProviders: this.setShouldRefreshProspectProviders,
      setStatus: this.setStatus,
      shouldRefreshMarketProviders: false,
      shouldRefreshNetNewProviders: false,
      shouldRefreshNetworkProviders: false,
      shouldRefreshProspectProviders: false,
      templateMetadata: null,
      updateRunConfigurationComment: this.updateProjectRunConfigurationComment.bind(this),
      updateRunConfigurationState: this.updateRunConfigurationState,
    };

    if (this.state.runConfiguration) {
      this.runConfigurationId = this.state.runConfiguration.projectRunConfigurationId;
    }
  }

  public async componentDidMount() {
    if ((parseQuerystring(this.props.location.search) as AdequacyQueryShape).viewBy) {
      this.setPerspective((parseQuerystring(this.props.location.search) as AdequacyQueryShape).viewBy, false);
    }
    this.props.subscribe(this.handleNotifications);
    await this.loadStates();
    await this.updateState();
    await this.updateResults();
  }

  public async componentDidUpdate(prevProps: CountyProjectContainerCombinedProps, prevState: CountyProjectContextProps) {
    let shouldUpdateResults = false;
    // Separate from our standard set of state and results is the tracking of the module tab.
    // We don't need to reload the universe when this happens.
    if (!isEqual(prevProps.location.pathname, this.props.location.pathname)) {
      const newModule = this.module(this.props.location.pathname);
      const oldModule = prevState.module;

      if (oldModule !== newModule) {
        const newAvailablePerspectives = this.getAvailablePerspectives(newModule, this.state.regionFilters);
        let newPerspective = this.state.perspective;
        // In case someone switches modules while ByAll or ByInspection are active
        if (!newAvailablePerspectives.includes(this.state.perspective)) {
          newPerspective = ProjectPerspective.ByState;
        }
        this.setState({
          availablePerspectives: newAvailablePerspectives,
          module: newModule,
          perspective: newPerspective,
        });
      }
    }

    // If the ApplyCMSApplicationCredit toggle is changed in gaps view,
    // or if it's turned off while ShowOnlyChanges is still enabled,
    // or if ShowOnlyChanges is changed,
    // we need to updateResults to run the filters again
    if ((prevState.cmsCreditIsApplying !== this.state.cmsCreditIsApplying &&
      (this.state.adequacyFilters.gaps || this.state.cmsCreditShowOnlyChanges)) ||
      prevState.cmsCreditShowOnlyChanges !== this.state.cmsCreditShowOnlyChanges) {
      shouldUpdateResults = true;
    }

    // Only trigger a full state update if our developer provided props change.
    const analysisTypeChanged = prevProps.query.opportunity !== this.props.query.opportunity;
    if (prevProps.projectId !== this.props.projectId || analysisTypeChanged) {
      const query = { ...this.props.query };
      this.setState({
        accuracyFilters: query,
        adequacyFilters: query,
        compositeFilters: query,
      }, async () => await this.updateState(false));
    }

    if (!this.state.loading && !this.state.loadedFilters &&
      (this.state.runConfiguration && this.state.runConfiguration.accuracyTemplateId ?
        this.state.loadedAccuracyTemplates : true) &&
      this.state.module) {
      this.updateFilterItems(this.props.location.pathname);
    }

    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.updateFilterItems(this.props.location.pathname);
      //refresh results if we switched between tabs(e.g. from Accuracy to Adequacy tab)
      shouldUpdateResults = true;
    }

    // Retrieve new adequacy results if run configuration changed.
    if (this.state.runConfiguration && (!isEqual(prevState.runConfiguration, this.state.runConfiguration) && this.state.loadedFilters) ||
      (!!this.state.runConfiguration && (prevState.loadedFilters !== this.state.loadedFilters) && Object.keys(this.state.regionFilters).length === 0)) {
      shouldUpdateResults = true;

      if (this.state.runConfiguration &&
        this.state.runConfiguration.companionProjectRunConfigurationId &&
        this.state.runConfiguration.memberSetAlias &&
        this.state.project.projectRunConfigurations.find((config) => config.projectRunConfigurationId === this.state.runConfiguration.companionProjectRunConfigurationId) &&
        this.state.initialDualMembershipMode) {
        this.setDualMembershipMode(true);
      } else {
        this.props.pushOrReplaceLocation({
          state: {
            ...this.props.location.state,
            initialDualMembershipMode: false
          }
        });
        this.setState({ initialDualMembershipMode: false });
      }
    }

    // Retrieve new adequacy results if run frequency changed.
    if (!isEqual(prevState.runFrequency, this.state.runFrequency)) {
      shouldUpdateResults = true;
    }

    // Retrieve new adequacy results if the region filters changed.
    if (!isEqual(prevState.regionFilters, this.state.regionFilters)) {
      shouldUpdateResults = true;
    }

    // Retrieve new adequacy results if the perspective changed.
    if (prevState.perspective !== this.state.perspective) {
      shouldUpdateResults = true;
    }

    // Retrieve new results if entering compare mode, because the trend chart needs to update
    // in the event of different timeslices having different regions/specialties
    // when entering/exiting compare mode
    if ((prevState.compareToRunConfiguration || this.state.compareToRunConfiguration) && !isEqual(prevState.compareToRunConfiguration, this.state.compareToRunConfiguration)) {
      shouldUpdateResults = true;
    }

    // Retrieve new adequacy results if any of our AdequacyFilters change.
    const oldAdequacyFilters = prevProps.query as AdequacyFilters;
    const newAdequacyFilters = this.props.query as AdequacyFilters;

    // Skip checking viewBy, as it's handled separately via the perspective state
    delete oldAdequacyFilters.viewBy;
    delete newAdequacyFilters.viewBy;

    // Retrieve new adequacy results if any of our AdequacyFilters change.
    if (!isEqual(oldAdequacyFilters, newAdequacyFilters)) {
      this.setState({
        accuracyFilters: { ...newAdequacyFilters },
        adequacyFilters: { ...newAdequacyFilters },
        compositeFilters: { ...newAdequacyFilters },
      }, async () => await this.updateResults());
    }

    if (this.state.runConfiguration && this.state.runConfiguration.dateCreated && !this.state.runConfigurationTimestamp) {
      const config = this.state.runConfiguration;
      this.setState({
        runConfigurationTimestamp: moment.utc(config.dateCreated),
      });
    }

    if (prevState.project && !prevState.project.isReadOnly && this.state.project && this.state.project.isReadOnly && this.state.runConfiguration) {
      const selectedTrendChartItem = this.state.adequacyTrendChartData.find((item) => item.projectRunConfigurationId === this.state.runConfiguration.projectRunConfigurationId);
      if (selectedTrendChartItem && [TrendChartItemType.Projected, TrendChartItemType.Termed, TrendChartItemType.ProjectedTermed].includes(selectedTrendChartItem.itemType)) {
        const latestPublished = this.state.adequacyTrendChartData.find((item) => item.itemType === TrendChartItemType.LatestPublished);
        this.setRunConfiguration(latestPublished.projectRunConfigurationId, moment(latestPublished.date), false, latestPublished.runFrequency, false, false);
      }
    }

    // If we're headed to Network Provider Maps from the Adequacy tab OR
    // we're coming back from Network Provider Maps and there are no filters set,
    // there is no need to update results.
    if ((this.props.location.pathname.includes("mapping") && prevState.module === "Adequacy") ||
      (this.state.module === "Mapping" && this.state.filters.length === 0)) {
      shouldUpdateResults = false;
    }

    if (shouldUpdateResults) {
      await this.updateResults();
    }
  }

  public componentWillUnmount() {
    this.props.unsubscribe(this.handleNotifications);

    removeLocalStorageRunConfigurationById(this.props.projectId);
  }

  public render() {
    // If a function is provided, pre-wrap with a Consumer for ease of use.
    // Otherwise, treat as standard children.
    let children: React.ReactNode;
    if (typeof this.props.children === "function") {
      children = (
        <CountyProjectContext.Consumer>
          {this.props.children as (value: CountyProjectContextProps) => React.ReactNode}
        </CountyProjectContext.Consumer>
      );
    } else {
      children = this.props.children;
    }

    const runConfiguration = this.state.runConfiguration;

    return (
      <CountyProjectContext.Provider value={this.state}>
        <AccuracyTemplateProvider
          onTemplateChange={this.handleAccuracyTemplateChange}
          templateCollectionId={runConfiguration && runConfiguration.accuracyTemplateCollectionId || ""}
          templateId={runConfiguration && runConfiguration.accuracyTemplateId || ""}
        >
          {children}
        </AccuracyTemplateProvider>
      </CountyProjectContext.Provider>
    );
  }

  private module(path: string) {
    let module: ProjectModule = null;

    if (path.includes("accuracy")) {
      module = "Accuracy";
    } else if (path.includes("adequacy")) {
      module = "Adequacy";
    } else if (path.includes("summary")) {
      module = "Summary";
    } else if (path.includes("mapping")) {
      module = "Mapping";
    }

    return module;
  }

  /* Forces the container to get EVERYTHING again...use sparingly and/or not at all. If you feel you need to, get someone else's opinion as well */
  private forceUpdateProject = async (): Promise<void> => {
    this.runConfigurationMap = {};
    await this.updateState();
  }

  private runProject = async (): Promise<void> => {
    if (this.state.project && !this.state.project.isRunning) {
      //Set loading state properties and let the notification context take care of reverting them when the project is done running
      this.setState((prevState) => ({ ...prevState, loading: true, loadingResults: true }), async () => {
        if (this.state.filters && this.state.filters.length) {
          this.removeAllFilters(this.state.filters.map((filter) => filter.urlPart));
        }
        await this.projectService.execute(this.props.projectId);
      });
    }
  }

  private requeueMarketAndProspectNCP = async (): Promise<void> => {
    if (this.state.project && !this.state.project.isRunning) {
      const project = this.setProjectReadOnlyProperties(false, true, true);

      project.isRunning = true;
      const partialState: Partial<CountyProjectContainerState> = {
        runConfiguration: cloneDeep(this.state.runConfiguration),
      };
      partialState.runConfiguration.status = ProjectRunConfigurationStatus.QueuedProspectMarketNcp;
      this.setState((prevState) => ({
        ...prevState,
        project,
        ...partialState,
      }), async () => {
        await this.nextClosestProviderService.requeueNextClosestProvider(this.props.projectId);
      });
    }
  }

  private rerunAccuracyGapAnalysis = async (projectId: number, projectRunConfigurationId: number): Promise<void> => {
    await this.accuracyGapAnalysisService.rerunAccuracyGapAnalysis(projectId, projectRunConfigurationId);
  }

  /**
   * Runs the projection analysis on the project.
   * If false and a projected run configuration is found in memory, that will be the selected run configuration.
   * If true and a projected run configuration is not found from the API, the latest run configuration will be the selected run configuration.
   */
  private processProviderChanges = async (reprocessFailure: boolean): Promise<void> => {
    if (this.state.project && !this.state.project.isRunning && this.state.processProviderChanges) {
      try {
        const project = this.setProjectReadOnlyProperties(true, true, true);

        project.isRunning = true;
        let latestPublishedConfiguration = project.projectRunConfigurations.find((prc) => prc.isLatestPublished && !prc.isProjected && !prc.isTermed && !prc.isProjectedTermed);
        const dualMembershipTypes = [ProjectRunConfigurationType.DualMembershipPrimary, ProjectRunConfigurationType.DualMembershipSupplemental];
        // basically, if the secondary membership was selected, we want to keep the secondary membership selected
        if (
          this.state.runConfiguration &&
          dualMembershipTypes.includes(this.state.runConfiguration.projectRunConfigurationType) &&
          dualMembershipTypes.includes(latestPublishedConfiguration.projectRunConfigurationType) &&
          latestPublishedConfiguration.projectRunConfigurationType !== this.state.runConfiguration.projectRunConfigurationType
        ) {
          latestPublishedConfiguration = project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === latestPublishedConfiguration.companionProjectRunConfigurationId);
        }
        let partialState: Partial<CountyProjectContainerState> = {
          runConfiguration: cloneDeep(this.state.runConfiguration),
        };
        if (latestPublishedConfiguration.projectRunConfigurationId !== this.state.runConfiguration.projectRunConfigurationId) {
          partialState = await this.setRunConfiguration(latestPublishedConfiguration.projectRunConfigurationId, undefined, false, undefined, true);
        }
        partialState.runConfiguration.status = ProjectRunConfigurationStatus.QueuedProviderChanges;
        this.setState((prevState) => ({
          ...prevState,
          loadingResults: true,
          project,
          ...partialState,
        }), async () => {
          await this.projectService.processProviderChanges(this.state.project.projectId, reprocessFailure);
        });

      } catch (error) {
        this.setState({ error });
      }
    }
  }

  private setRegionFilters = async (regionFilters: RegionFilters, isCancelled: () => boolean) => {
    if (!isEqual(regionFilters, this.state.regionFilters) || !this.state.loadedFilters) {
      if (!isCancelled()) {
        this.setState((prevState) => {
          const availablePerspectives = this.getAvailablePerspectives(this.state.module, regionFilters);

          let perspective = (parseQuerystring(this.props.location.search) as AdequacyQueryShape).viewBy as ProjectPerspective || ProjectPerspective.ByState;
          if (availablePerspectives.indexOf(perspective) === -1) {
            perspective = availablePerspectives[0];
          }
          return ({
            ...prevState,
            availablePerspectives,
            loadedFilters: true,
            perspective,
            regionFilters,
          });
        }, async () => {
          const hasImpact = await this.doesProjectHaveImpactProviders();
          this.setState({
            hasProspectOrMarketProviders: hasImpact,
          });
        });
      }
    }
  }

  private updateFilterItems = (pathname: string): void => {
    const isComparingResults = !!this.state.compareToRunConfiguration;

    if (!this.state.runConfiguration || (isComparingResults && !this.state.compareToRunConfiguration)) {
      return;
    }

    let filters = [...this.state.filters];
    let countyFilter: RegionFilterItem;
    let inspectionFilter: RegionFilterItem;
    let bandFilter: RegionFilterItem;
    let specialtyFilter: RegionFilterItem;
    let stateFilter: RegionFilterItem;

    filters.forEach((f) => {
      switch (f.filterType) {
        case FilterType.County:
          countyFilter = f;
          break;
        case FilterType.Inspection:
          inspectionFilter = f;
          break;
        case FilterType.Specialty:
          specialtyFilter = f;
          break;
        case FilterType.State:
          stateFilter = f;
          break;
        default:
          break;
      }
    });

    const badRegionFilters: RegionFilterItem[] = [];
    const regionFilters = parseRegionFilters(pathname);
    const newCountyFilter = regionFilters.county;
    const newInspectionFilter = regionFilters.inspection;
    const newBandFilter = regionFilters.band;
    const newSpecialtyFilter = regionFilters.specialty ? decodeURIComponent(regionFilters.specialty) : regionFilters.specialty;
    const newStateFilter = regionFilters.state;

    // county filter
    if (!!newCountyFilter) {
      if (!!countyFilter && newCountyFilter !== countyFilter.filterValue) {
        filters = filters.filter((f) => f.filterType !== FilterType.County);
      }

      if (!countyFilter || newCountyFilter !== countyFilter.filterValue) {
        const countyResultRegion = {
          regionCode: newCountyFilter,
          regionGranularity: RegionGranularity.County,
          subRegionCode: "",
        } as ResultRegion;

        const availableCountyStatuses = this.state.compareToRunConfiguration
          ? union(this.state.runConfiguration.getAvailableRegionStatusTypes(countyResultRegion),
            this.state.compareToRunConfiguration.getAvailableRegionStatusTypes(countyResultRegion))
          : this.state.runConfiguration.getAvailableRegionStatusTypes(countyResultRegion);
        if (this.state.networkAssessmentAnalysisType === NetworkAssessmentAnalysisType.ServiceArea) {
          if (availableCountyStatuses.length > 0 && availableCountyStatuses.filter((countyStatus) => countyStatus !== RegionSetStatusType.Opportunity).length === 0) {
            //filtered to active with only opportunity counties
            let query = parseQuerystring(this.props.location.search) as RegionQueryShape;
            query = {
              ...query,
              active: undefined,
              approved: undefined,
              opportunity: true.toString(),
              pending: undefined,
            };

            //force the user to the correct region status and exit
            const qs = querystringify(query as { [key: string]: string });
            this.props.pushOrReplaceLocation({ search: qs });

            return;
          }
        } else {
          if (availableCountyStatuses.length > 0 && availableCountyStatuses.indexOf(RegionSetStatusType.Opportunity) === -1) {
            //filtered to opportunity with no opportunity counties

            let query = parseQuerystring(this.props.location.search) as RegionQueryShape;
            query = {
              ...query,
              active: true.toString(),
              approved: true.toString(),
              opportunity: undefined,
              pending: true.toString(),
            };

            //force the user to the correct region status and exit
            const qs = querystringify(query as { [key: string]: string });
            this.props.pushOrReplaceLocation({ search: qs });

            return;
          }
        }

        countyFilter = this.buildFilter(newCountyFilter, FilterType.County);

        let county = this.state.runConfiguration.getCounty(newCountyFilter);
        county = !county && isComparingResults
          ? this.state.compareToRunConfiguration.getCounty(newCountyFilter)
          : county;

        countyFilter.displayText = county
          ? `${county.regionCode} - ${county.displayName}`
          : null;

        if (!county) {
          badRegionFilters.push(countyFilter);
        } else {
          filters.push(countyFilter);
        }
      }
    } else { //!newCountyFilter
      if (!!countyFilter) {
        filters = filters.filter((f) => f.filterType !== FilterType.County);
      }
    }

    // inspection filter
    if (!inspectionFilter && newInspectionFilter && this.state.accuracyTemplate) {
      inspectionFilter = this.buildFilter(newInspectionFilter, FilterType.Inspection);
      if (!bandFilter && newBandFilter && this.state.accuracyTemplate) {
        bandFilter = this.buildFilter(newBandFilter, FilterType.Band);
        inspectionFilter.urlPart += bandFilter.urlPart;
      }

      const inspections: AccuracyTemplateInspection[] = [];

      for (const group of this.state.accuracyTemplate.criteria.inspectionGroups) {
        for (const insp of group.inspections) {
          inspections.push(insp);
        }
      }

      let inspection: AccuracyTemplateInspection = inspections.find((i) => i.uid === inspectionFilter.filterValue);

      inspection = !inspection && isComparingResults
        ? inspections.find((i) => i.uid === inspectionFilter.filterValue)
        : inspection;

      inspectionFilter.displayText = inspection ?
        bandFilter ?
          `${inspection.className.split('.')[0]}: ${inspection.name} (${bandDisplayNames(bandFilter.filterValue)})`
          : inspection.name
        : null;

      if (!inspection) {
        badRegionFilters.push(inspectionFilter);
      } else {
        filters.push(inspectionFilter);
      }
    } else if (inspectionFilter && !newInspectionFilter) {
      filters = filters.filter((f) => f.filterType !== FilterType.Inspection);
    }

    // specialty filter
    if (newSpecialtyFilter && (!specialtyFilter || newSpecialtyFilter !== specialtyFilter.filterValue)) {

      //If we have an old specialty filter, remove it before introducing replacement.
      if (!!specialtyFilter) {
        filters = filters.filter((f) => f.filterType !== FilterType.Specialty);
      }

      specialtyFilter = this.buildFilter(newSpecialtyFilter, FilterType.Specialty);

      let specialtyGroup = this.state.runConfiguration.specialtyGroups
        .find((sg) => sg.code === specialtyFilter.filterValue);
      specialtyGroup = !specialtyGroup && isComparingResults
        ? this.state.compareToRunConfiguration.specialtyGroups
          .find((sg) => sg.code === specialtyFilter.filterValue)
        : specialtyGroup;

      if (specialtyGroup) {
        specialtyFilter.displayText = `${specialtyGroup.code} - ${specialtyGroup.name}`;
      }

      if (!specialtyGroup) {
        badRegionFilters.push(specialtyFilter);
      } else {
        filters.push(specialtyFilter);
      }
    } else if (specialtyFilter && !newSpecialtyFilter) {
      filters = filters.filter((f) => f.filterType !== FilterType.Specialty);
    }

    // state filter
    if (!!newStateFilter) {
      if (!!stateFilter && newStateFilter !== stateFilter.filterValue) {
        filters = filters.filter((f) => f.filterType !== FilterType.State);
      }

      if (!stateFilter || newStateFilter !== stateFilter.filterValue) {
        const stateResultRegion = {
          regionCode: newStateFilter,
          regionGranularity: RegionGranularity.State,
          subRegionCode: "",
        } as ResultRegion;

        const availableCountyStatuses = this.state.compareToRunConfiguration
          ? union(this.state.runConfiguration.getAvailableRegionStatusTypes(stateResultRegion),
            this.state.compareToRunConfiguration.getAvailableRegionStatusTypes(stateResultRegion))
          : this.state.runConfiguration.getAvailableRegionStatusTypes(stateResultRegion);
        if (this.state.networkAssessmentAnalysisType === NetworkAssessmentAnalysisType.ServiceArea) {
          if (availableCountyStatuses.length > 0 && availableCountyStatuses.filter((countyStatus) => countyStatus !== RegionSetStatusType.Opportunity).length === 0) {
            //filtered to active with only opportunity counties in state
            let query = parseQuerystring(this.props.location.search) as RegionQueryShape;
            query = {
              ...query,
              active: undefined,
              approved: undefined,
              opportunity: true.toString(),
              pending: undefined,
            };

            //force the user to the correct region status and exit
            const qs = querystringify(query as { [key: string]: string });
            this.props.pushOrReplaceLocation({ search: qs });

            return;
          }
        } else {
          if (availableCountyStatuses.length > 0 && availableCountyStatuses.indexOf(RegionSetStatusType.Opportunity) === -1) {
            //filtered to opportunity with no opportunity counties in state

            let query = parseQuerystring(this.props.location.search) as RegionQueryShape;
            query = {
              ...query,
              active: true.toString(),
              approved: true.toString(),
              opportunity: undefined,
              pending: true.toString(),
            };

            //force the user to the correct region status and exit
            const qs = querystringify(query as { [key: string]: string });
            this.props.pushOrReplaceLocation({ search: qs });

            return;
          }
        }

        stateFilter = this.buildFilter(newStateFilter, FilterType.State);

        let state = this.state.runConfiguration.getState(newStateFilter);
        state = !state && isComparingResults
          ? this.state.compareToRunConfiguration.getState(newStateFilter)
          : state;

        stateFilter.displayText = state
          ? `${this.statesMap[state.regionCode]} - ${state.displayName}`
          : null;

        if (!state) {
          badRegionFilters.push(stateFilter);
        } else {
          filters.push(stateFilter);
        }
      }
    } else { //!newStateFilter
      if (!!stateFilter) {
        filters = filters.filter((f) => f.filterType !== FilterType.State);
      }
    }

    if (badRegionFilters.length > 0) {
      let newPathName = pathname;
      badRegionFilters.forEach((bf) => newPathName = newPathName.replace(bf.urlPart, ""));
      this.props.pushOrReplaceLocation({ pathname: newPathName });
    } else {
      this.setState((prevState) => ({
        ...prevState,
        filters: filters,
      }), () => {
        this.setRegionFilters(regionFilters, () => this.isCancelled);
      });
    }
  }

  private buildFilter = (filterValue: string, filterType: FilterType): RegionFilterItem => {
    return {
      filterType,
      filterValue,
      urlPart: `${filterType}${encodeURIComponent(filterValue)}`,
    };
  }

  private clearFilters = (): void => {
    this.setState({ filters: [] });
  }

  private removeFilter = (urlPart: string): void => {
    const pathname = this.props.location.pathname;
    const updatedPathname = pathname.replace(urlPart, "");
    this.props.pushOrReplaceLocation({ pathname: updatedPathname });
  }

  private removeAllFilters = (urlParts: string[]): void => {
    let updatedPathname = clone(this.props.location.pathname);
    for (const urlPart of urlParts) {
      updatedPathname = updatedPathname.replace(urlPart, "");
    }

    this.props.pushOrReplaceLocation({ pathname: updatedPathname });
  }

  private loadStates = async () => {
    const states = await new GeographyService().getStates();

    states.forEach((s) => {
      this.statesMap[s.abbreviation] = s.stateSSA;
    });

  }

  private getAvailablePerspectives = (module: ProjectModule, regionFilters: RegionFilters) => {
    let initialPerspectives: ProjectPerspective[] = [];
    switch (module) {
      case "Accuracy":
        initialPerspectives = [ProjectPerspective.ByState, ProjectPerspective.ByCounty, ProjectPerspective.BySpecialty, ProjectPerspective.ByInspection, ProjectPerspective.All];
        break;
      case "Adequacy":
        initialPerspectives = [ProjectPerspective.ByState, ProjectPerspective.ByCounty, ProjectPerspective.BySpecialty, ProjectPerspective.All];
        break;
      case "Summary":
        initialPerspectives = [];
        break;
    }

    if (!regionFilters || (!regionFilters.county && !regionFilters.specialty && !regionFilters.state)) {
      return initialPerspectives;
    }

    const availablePerspectives: ProjectPerspective[] = [];

    if (regionFilters.county && regionFilters.specialty) {
      availablePerspectives.push(ProjectPerspective.All);
    }

    if (regionFilters.county) {
      //the dropdown does not show for counties, but the perspective change is necessary
      //when in by-both view in order to get the correct results
      if (!regionFilters.specialty) {
        availablePerspectives.push(ProjectPerspective.BySpecialty);
      }

      if (module === "Accuracy") {
        availablePerspectives.push(ProjectPerspective.ByInspection);
      }

      return availablePerspectives;
    }

    if (!regionFilters.state) {
      availablePerspectives.push(ProjectPerspective.ByState);
    }

    availablePerspectives.push(ProjectPerspective.ByCounty);

    if (!regionFilters.specialty) {
      availablePerspectives.push(ProjectPerspective.BySpecialty);

      if (module === "Adequacy") {
        availablePerspectives.push(ProjectPerspective.All);
      }
    }

    if (!regionFilters.inspection && module === "Accuracy") {
      availablePerspectives.push(ProjectPerspective.ByInspection);
      availablePerspectives.push(ProjectPerspective.All);
    }

    if (regionFilters.specialty && regionFilters.inspection && module === "Accuracy") {
      availablePerspectives.push(ProjectPerspective.ByInspection);
      availablePerspectives.push(ProjectPerspective.All);
    }

    return availablePerspectives;
  }

  private setPerspective = async (perspective: ProjectPerspective, forceRefresh: boolean) => {
    if ((forceRefresh || !this.state.loadedDefaultPerspective) && perspective !== this.state.perspective) {
      this.setState((prevState) => ({
        ...prevState,
        loadedDefaultPerspective: true,
        perspective
      }));
    }
  }

  private setStatus = async (region: RegionSetQualifier, newStatus: RegionSetStatusType) => {
    if (this.state.project && !this.state.project.isRunning && this.state.runConfiguration) {
      this.setState({ error: null, loadingResults: true }, async () => {
        try {

          const [newAccuracyRecord, newAdequacyRecord, newAttestationRecord] = await this.state.runConfiguration.setStatus(region, newStatus);

          const accuracyResult = { ...this.state.accuracyResult };
          const adequacyResult = { ...this.state.adequacyResult };
          const attestationResult = { ...this.state.attestationResult };
          const compositeResult = { ...this.state.compositeResult };

          accuracyResult.aggregateDetails = newAccuracyRecord ? [...accuracyResult.aggregateDetails.filter((r) => r.key !== newAccuracyRecord.key), newAccuracyRecord] : accuracyResult.aggregateDetails;
          adequacyResult.aggregateDetails = newAdequacyRecord ? [...adequacyResult.aggregateDetails.filter((r) => r.key !== newAdequacyRecord.key), newAdequacyRecord] : adequacyResult.aggregateDetails;
          attestationResult.aggregateDetails = newAttestationRecord ? [...attestationResult.aggregateDetails.filter((r) => r.key !== newAttestationRecord.key), newAttestationRecord] : attestationResult.aggregateDetails;

          const compositeAggregateDetails: CompositeAggregate[] = [];

          compositeResult.aggregateDetails = compositeAggregateDetails;

          this.setState((prevState) => ({
            ...prevState,
            accuracyResult,
            adequacyResult,
            attestationResult,
            compositeResult,
            loadingResults: false,
          }));

        } catch (error) {
          console.error(error);
          this.setState({ error });
        }
      });
    }
  }

  private setBulkStatus = async (regions: RegionSetQualifier[], newStatus: RegionSetStatusType) => {
    if (this.state.project && !this.state.project.isRunning && this.state.runConfiguration) {
      this.setState({ error: null, loadingResults: true },
        async () => {
          try {
            regions.forEach(async (region) => {
              const [newAccuracyRecord, newAdequacyRecord, newAttestationRecord] = await this.state.runConfiguration.setStatus(region, newStatus);

              const accuracyResult = { ...this.state.accuracyResult };
              const adequacyResult = { ...this.state.adequacyResult };
              const attestationResult = { ...this.state.attestationResult };
              const compositeResult = { ...this.state.compositeResult };

              accuracyResult.aggregateDetails = newAccuracyRecord ? [...accuracyResult.aggregateDetails.filter((r) => r.key !== newAccuracyRecord.key), newAccuracyRecord] : accuracyResult.aggregateDetails;
              adequacyResult.aggregateDetails = newAdequacyRecord ? [...adequacyResult.aggregateDetails.filter((r) => r.key !== newAdequacyRecord.key), newAdequacyRecord] : adequacyResult.aggregateDetails;
              attestationResult.aggregateDetails = newAttestationRecord ? [...attestationResult.aggregateDetails.filter((r) => r.key !== newAttestationRecord.key), newAttestationRecord] : attestationResult.aggregateDetails;
              compositeResult.aggregateDetails = []; //[...compositeResult.aggregateDetails.filter((r) => r.key !== newCompositeRecord.key), newCompositeRecord];

              this.setState((prevState) => ({
                ...prevState,
                accuracyResult,
                adequacyResult,
                attestationResult,
                compositeResult,
                loadingResults: false,
              }));
            }, this);
            this.forceUpdateProject();
          } catch (error) {
            console.error(error);
            this.setState({ error });
          }
        });
    }
  }

  private setDualMembershipMode = async (isDualMembershipMode: boolean, projectRunConfigurationType?: ProjectRunConfigurationType, primaryRunConfigurationId?: number, compareToRunConfigurationId?: number, forceLoading?: boolean) => {
    let newConfigurationState: Partial<CountyProjectContainerState> = {};
    let primaryConfiguration: ProjectRunConfiguration;
    let compareToRunConfiguration: ProjectRunConfiguration;
    if (primaryRunConfigurationId) {
      this.state.project.projectRunConfigurations.some((config) => {
        if (primaryConfiguration && compareToRunConfiguration) {
          return true;
        } else if (config.projectRunConfigurationId === primaryRunConfigurationId) {
          primaryConfiguration = config;
        } else if (config.projectRunConfigurationId === compareToRunConfigurationId) {
          compareToRunConfiguration = config;
        }
      });
    } else {
      primaryConfiguration = projectRunConfigurationType !== this.state.runConfiguration.projectRunConfigurationType ?
        this.state.project.projectRunConfigurations.find((config) => config.projectRunConfigurationId === this.state.runConfiguration.companionProjectRunConfigurationId) :
        this.state.runConfiguration;
    }

    if (isDualMembershipMode) {
      const companionRunConfiguration = this.state.project.projectRunConfigurations.find((config) => config.projectRunConfigurationId === primaryConfiguration.companionProjectRunConfigurationId);
      if (primaryConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary) {
        newConfigurationState = await this.setRunConfiguration(primaryConfiguration.projectRunConfigurationId, moment.utc(primaryConfiguration.dateCreated), false, undefined, true, forceLoading);
        Object.assign(newConfigurationState, await this.setRunConfiguration(companionRunConfiguration.projectRunConfigurationId, moment.utc(companionRunConfiguration.dateCreated), true, undefined, true, forceLoading));
      } else if (companionRunConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary) {
        newConfigurationState = await this.setRunConfiguration(companionRunConfiguration.projectRunConfigurationId, moment.utc(companionRunConfiguration.dateCreated), false, undefined, true, forceLoading);
        Object.assign(newConfigurationState, await this.setRunConfiguration(primaryConfiguration.projectRunConfigurationId, moment.utc(primaryConfiguration.dateCreated), true, undefined, true, forceLoading));
      }

      const query = clone(this.props.query);
      // remove run configuration type from query string
      delete query.projectRunConfigurationType;
      // as unknown as * because it doesn't see an index signature for type string in AdequacyFilters & RunConfigurationTypeProps
      // when all the keys are strings
      const qs = querystringify(query as unknown as { [key: string]: unknown });

      this.props.pushOrReplaceLocation({
        search: qs,
        state: {
          ...this.props.location.state,
          initialDualMembershipMode: false,
        },
      });
    } else {
      const selectedConfig = primaryConfiguration && primaryConfiguration.projectRunConfigurationType === projectRunConfigurationType ? primaryConfiguration : compareToRunConfiguration;
      const compareToConfig = compareToRunConfigurationId ?
        selectedConfig === primaryConfiguration ? compareToRunConfiguration : primaryConfiguration :
        undefined;

      if (selectedConfig) {
        newConfigurationState = await this.setRunConfiguration(selectedConfig.projectRunConfigurationId, moment(selectedConfig.dateCreated), false, undefined, true, forceLoading);
        Object.assign(newConfigurationState, await this.setRunConfiguration(compareToConfig && compareToConfig.projectRunConfigurationId, compareToConfig && moment(compareToConfig.dateCreated), true, undefined, true, forceLoading));
      }
      let query = parseQuerystring(this.props.location.search) as RunConfigurationTypeProps;
      query = {
        ...query,
        projectRunConfigurationType,
      };
      //set run configuration type in query string
      const qs = querystringify(query as unknown as { [key: string]: unknown });
      this.props.pushOrReplaceLocation({ search: qs });
    }
    this.updateRunConfigurationState({
      ...newConfigurationState,
      initialDualMembershipMode: false,
      isDualMembershipMode,
    });
  }

  private setRunConfiguration = async (runConfigurationId: number, runConfigurationTimestamp?: moment.Moment, isCompareTo?: boolean, runFrequency?: TimeFrequency, delayUpdate?: boolean, forceLoading?: boolean) => {
    this.setLoading(true);

    try {
      this.runConfigurationId = isCompareTo ? this.runConfigurationId : runConfigurationId;
      let accuracyResult: AccuracyResult;
      let adequacyResult: AdequacyResult;
      let attestationResult: AttestationResult;
      let compositeResult: CompositeResult;

      let runConfiguration: ProjectRunConfigurationResult;

      const runKey = keyFor(runConfigurationId, this.state.networkAssessmentAnalysisType); // TODO: Explicitly make `networkAssessmentAnalysisType` nullable.
      runConfiguration = this.runConfigurationMap[runKey];

      if (!runConfiguration) {
        runConfiguration = await createRunConfigurationResult(
          runConfigurationId,
          this.state.project,
          this.projectService,
          this.specialtyGroupExceptionsService,
          this.accuracyService,
          this.attestationService,
          this.templateService,
          this.geographyService,
          this.state.networkAssessmentAnalysisType,
        );
      }

      if (!runConfiguration || (runConfiguration.status !== ProjectRunConfigurationStatus.Succeeded && runConfiguration.status !== ProjectRunConfigurationStatus.Failed && runConfiguration.status !== ProjectRunConfigurationStatus.QueuedProspectMarketNcp
        && runConfiguration.status !== ProjectRunConfigurationStatus.ProcessingProspectMarketNcp && runConfiguration.status !== ProjectRunConfigurationStatus.FailedProspectMarketNcp
        && !this.state.project.isEditRestricted)) {

        runConfiguration = undefined;
      }

      if (runConfiguration) {
        this.runConfigurationMap[runKey] = runConfiguration;

        // The ByInspection perspective is special.
        // Operate as if we're under ByState.
        // TODO: Consider an alternative way...
        const perspective = this.state.perspective === ProjectPerspective.ByInspection ? ProjectPerspective.ByState : this.state.perspective;
        const currentModule = this.module(this.props.location.pathname);

        accuracyResult = await getUpdatedAccuracyResultsHelper(
          runConfiguration,
          perspective,
          this.state.regionFilters,
          this.state.accuracyFilters,
          this.accuracyService,
          this.state.project.projectId,
          currentModule
        );

        accuracyResult = {
          aggregateDetails: accuracyResult && accuracyResult.aggregateDetails || undefined,
          aggregateSummary: accuracyResult && accuracyResult.aggregateSummary || undefined,
          availableRegionSetStatusTypes: accuracyResult && accuracyResult.availableRegionSetStatusTypes || undefined,
          details: accuracyResult && accuracyResult.details || undefined,
        };

        if (isCompareTo) {
          const updatedResults = await getUpdatedAdequacyResults(
            runConfiguration,
            this.state.compareToRunConfiguration,
            this.state.perspective,
            this.state.regionFilters,
            this.state.adequacyFilters,
            this.state.cmsCreditIsApplying,
            this.state.cmsCreditShowOnlyChanges,
            currentModule
          );
          adequacyResult = updatedResults.adequacyResult;
        } else {
          adequacyResult = await getUpdatedAdequacyResultsHelper(
            runConfiguration,
            this.state.perspective,
            this.state.regionFilters,
            this.state.adequacyFilters,
            currentModule
          );
        }

        adequacyResult = {
          ...adequacyResult,
          aggregateDetails: adequacyResult && adequacyResult.aggregateDetails || undefined,
          aggregateSummary: adequacyResult && adequacyResult.aggregateSummary || undefined,
          availableRegionSetStatusTypes: adequacyResult && adequacyResult.availableRegionSetStatusTypes || undefined,
          details: adequacyResult && adequacyResult.details || undefined,
        };

        attestationResult = await getUpdatedAttestationResultsHelper(
          runConfiguration,
          perspective,
          this.state.regionFilters,
        );

        attestationResult = {
          aggregateDetails: attestationResult && attestationResult.aggregateDetails || undefined,
          aggregateSummary: attestationResult && attestationResult.aggregateSummary || undefined,
          details: attestationResult && attestationResult.details || undefined,
        };

        const mergedAggregateDetails: CompositeAggregate[] = [];
        mergedAggregateDetails.push({ ...adequacyResult.aggregateDetails[0], ...attestationResult.aggregateDetails[0], ...accuracyResult.aggregateDetails[0] });
        const mergedAggregateSummary: CompositeAggregate = { ...adequacyResult.aggregateSummary, ...attestationResult.aggregateSummary, ...accuracyResult.aggregateSummary };
        const mergedDetails: CompositeDetail[] = [];
        mergedDetails.push({ ...accuracyResult.details[0], ...adequacyResult.details[0], ...attestationResult.details[0] });

        compositeResult = {
          aggregateDetails: mergedAggregateDetails || undefined,
          aggregateSummary: mergedAggregateSummary || undefined,
          details: mergedDetails || undefined,
        };
      } else {
        accuracyResult = { ...defaultAccuracyResult };
        adequacyResult = { ...defaultAdequacyResult };
        attestationResult = { ...defaultAttestationResult };
        compositeResult = { ...defaultCompositeResult };
      }

      if (isCompareTo) {
        //set the compare to run configuration timestamp if it wasn't passed in. It is not populated when compare mode is initially toggled on.
        const compareToRunConfigurationTimestamp = runConfigurationTimestamp || (runConfiguration ? moment.utc(runConfiguration.dateCreated) : null);

        const newCompareState: Partial<CountyProjectContainerState> = {
          compareToAccuracyResult: accuracyResult,
          compareToAdequacyResult: adequacyResult,
          compareToAttestationResult: attestationResult,
          compareToCompositeResult: compositeResult,
          compareToRunConfiguration: runConfiguration,
          compareToRunConfigurationTimestamp,
          loading: forceLoading ? forceLoading : false,
          loadingResults: forceLoading ? forceLoading : false,
          runFrequency: runFrequency || this.state.runFrequency,
        };

        if (!delayUpdate) {
          this.updateRunConfigurationState(newCompareState);
        }

        return newCompareState;
      } else {
        const newState: Partial<CountyProjectContainerState> = {
          accuracyResult,
          adequacyResult,
          attestationResult,
          compositeResult,
          loading: forceLoading ? forceLoading : false,
          loadingResults: forceLoading ? forceLoading : false,
          runConfiguration,
          runConfigurationTimestamp,
          runFrequency: runFrequency || this.state.runFrequency,
        };

        if (!delayUpdate) {
          this.updateRunConfigurationState(newState);
        }

        return newState;
      }

    } catch (error) {
      console.error(error);
      this.setState({ error });
    }
  }

  private updateRunConfigurationState = async (newState: Partial<CountyProjectContainerState>) => {
    const switchingToDualMembership = this.state.runConfiguration && this.state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.Standard &&
      newState.runConfiguration && newState.runConfiguration.projectRunConfigurationType !== ProjectRunConfigurationType.Standard;

    if (switchingToDualMembership) {
      const compareToRunConfiguration = await createRunConfigurationResult(
        newState.runConfiguration.companionProjectRunConfigurationId,
        this.state.project,
        this.projectService,
        this.specialtyGroupExceptionsService,
        this.accuracyService,
        this.attestationService,
        this.templateService,
        this.geographyService,
        this.state.networkAssessmentAnalysisType,
      );
      newState.compareToRunConfiguration = compareToRunConfiguration;
    }

    this.props.pushOrReplaceLocation({
      state: {
        ...this.props.location.state,
        compareToRunConfiguration: newState.compareToRunConfiguration,
        isDualMembershipMode: switchingToDualMembership ||
          (!!newState.runConfiguration &&
            !!newState.runConfiguration.companionProjectRunConfigurationId &&
            !!newState.compareToRunConfiguration &&
            newState.compareToRunConfiguration.projectRunConfigurationId === newState.runConfiguration.companionProjectRunConfigurationId
          ),
        runConfiguration: newState.runConfiguration !== undefined ? newState.runConfiguration : this.state.runConfiguration,
      },
    });

    this.setState((prevState: CountyProjectContainerState) => ({
      ...prevState,
      isDualMembershipMode:
        switchingToDualMembership ||
        (!!newState.runConfiguration &&
          !!newState.runConfiguration.companionProjectRunConfigurationId &&
          !!newState.compareToRunConfiguration &&
          newState.compareToRunConfiguration.projectRunConfigurationId === newState.runConfiguration.companionProjectRunConfigurationId
        ),
      loading: false,
      loadingResults: false,
      ...newState,
    }));
  }

  private setException = async (exception: SpecialtyGroupException) => {
    if (this.state.project && !this.state.project.isRunning && this.state.runConfiguration) {
      try {
        const [key, updatedException] = await this.state.runConfiguration.setException(exception);

        const projectClone = cloneDeep(this.state.project);
        const runConfigClone = cloneDeep(this.state.runConfiguration);

        projectClone.hasUnprocessedSpecialtyGroupExceptions = projectClone.hasUnprocessedSpecialtyGroupExceptions || updatedException.isUnprocessed;
        runConfigClone.regionSpecialtyGroupExceptions[key] = updatedException;
        runConfigClone.clearAdequacyDetails(runConfigClone.getCounty(exception.regionCode));

        this.setState({
          project: projectClone,
          runConfiguration: runConfigClone,
        });
      } catch (error) {
        this.setState({ error });
      }
    }
  }

  private setLoading = (loading: boolean) => {
    this.setState({
      loading,
      loadingResults: loading,
    });
  }

  private async updateResults() {
    if (this.state.project && (!this.state.project.isRunning || this.state.project.isEditRestricted) && this.state.runConfiguration) {
      this.setState({ error: null, loadingResults: true }, async () => {
        // The ByInspection perspective is special.
        // Operate as if we're under ByState.
        // TODO: Consider an alternative way...
        const perspective = this.state.perspective === ProjectPerspective.ByInspection ? ProjectPerspective.ByState : this.state.perspective;

        try {
          const regionFilters = { ...this.state.regionFilters };
          if (!regionFilters || !(regionFilters.county || regionFilters.specialty || regionFilters.state)) {
            //if there are no region filters, make sure there are results for this analysis type, and if not, and the opposite analysis type does have results
            //redirect the user to the opposite analysis type
            //if there are filters this work was already done
            const availableCountyStatuses = this.state.compareToRunConfiguration
              ? union(this.state.runConfiguration.getAvailableRegionStatusTypes(this.state.runConfiguration.serviceArea),
                this.state.compareToRunConfiguration.getAvailableRegionStatusTypes(this.state.runConfiguration.serviceArea))
              : this.state.runConfiguration.getAvailableRegionStatusTypes(this.state.runConfiguration.serviceArea);

            if (this.state.networkAssessmentAnalysisType === NetworkAssessmentAnalysisType.ServiceArea) {
              if (availableCountyStatuses.length > 0 && availableCountyStatuses.filter((countyStatus) => countyStatus !== RegionSetStatusType.Opportunity).length === 0) {
                //filtered to active with only opportunity counties in state
                let query = parseQuerystring(this.props.location.search) as RegionQueryShape;
                query = {
                  ...query,
                  active: undefined,
                  approved: undefined,
                  opportunity: true.toString(),
                  pending: undefined,
                };

                //force the user to the correct region status and exit
                const qs = querystringify(query as { [key: string]: unknown });
                this.props.pushOrReplaceLocation({ search: qs });

                return;
              }
            } else {
              if (availableCountyStatuses.length > 0 && availableCountyStatuses.indexOf(RegionSetStatusType.Opportunity) === -1) {
                //filtered to opportunity with no opportunity counties in state

                let query = parseQuerystring(this.props.location.search) as RegionQueryShape;
                query = {
                  ...query,
                  active: true.toString(),
                  approved: true.toString(),
                  opportunity: undefined,
                  pending: true.toString(),
                };

                //force the user to the correct region status and exit
                const qs = querystringify(query as { [key: string]: unknown });
                this.props.pushOrReplaceLocation({ search: qs });

                return;
              }
            }
          }

          const loadingTrackerId = uniqueId("container-tracker");
          this.currentLoadingTrackerId = loadingTrackerId;
          const currentModule = this.module(this.props.location.pathname);
          const useLargeProjectEnhancements = this.props.enabledFeatureFlags.has(Features.ACCURACY_LARGE_PROJECT_PERFORMANCE_ENHANCEMENTS);
          const accuracyResultsPromise = getUpdatedAccuracyResults(
            this.state.runConfiguration,
            this.state.compareToRunConfiguration,
            perspective,
            this.state.regionFilters,
            this.state.accuracyFilters,
            this.accuracyService,
            this.state.project.projectId,
            currentModule,
            useLargeProjectEnhancements
          );
          const adequacyResultsPromise = getUpdatedAdequacyResults(
            this.state.runConfiguration,
            this.state.compareToRunConfiguration,
            perspective,
            this.state.regionFilters,
            this.state.adequacyFilters,
            this.state.cmsCreditIsApplying,
            this.state.cmsCreditShowOnlyChanges,
            currentModule
          );

          // if the current config is canceled, use the latest published
          const runConfiguration = cloneDeep(this.state.runConfiguration);
          const compareToRunConfiguration = cloneDeep(this.state.compareToRunConfiguration);
          Promise.all([accuracyResultsPromise, adequacyResultsPromise]).then(async (res) => {
            const accuracyResults = res[0];
            const adequacyResults = res[1];

            const attestationResults = await getUpdatedAttestationResults(
              runConfiguration,
              compareToRunConfiguration,
              perspective,
              this.state.regionFilters,
            );

            const compositeResults = await getUpdatedCompositeResults(
              runConfiguration,
              compareToRunConfiguration,
              perspective,
              this.state.regionFilters,
              this.state.compositeFilters,
            );

            let regionSetQualifier: RegionSetQualifier;
            let summaryPartial: { specialtyGroupCode?: string; specialtyGroupDisplayOrder?: number; specialtyGroupEntityType?: SpecialtyGroupEntityType; specialtyGroupIsInformational?: boolean; specialtyGroupName?: string };
            switch (currentModule) {
              case "Accuracy":
                regionSetQualifier = accuracyResults.accuracyResult.aggregateSummary;
                summaryPartial = accuracyResults.accuracyResult.aggregateSummary;
                break;
              case "Adequacy":
                regionSetQualifier = adequacyResults.adequacyResult.aggregateSummary;
                summaryPartial = adequacyResults.adequacyResult.aggregateSummary;
                break;
              case "Summary":
                // TODO: Don't just fallback to adequacy.
                regionSetQualifier = adequacyResults.adequacyResult.aggregateSummary;
                summaryPartial = adequacyResults.adequacyResult.aggregateSummary;
                break;
            }

            const analysisSpecialtyGroup: AnalysisSpecialtyGroup = summaryPartial ? {
              code: summaryPartial.specialtyGroupCode,
              displayOrder: summaryPartial.specialtyGroupDisplayOrder,
              entityType: summaryPartial.specialtyGroupEntityType,
              isInformational: summaryPartial.specialtyGroupIsInformational,
              name: summaryPartial.specialtyGroupName,
              suppressSpecialtyAccessResults: false,
            } : null;

            const inspection = this.state.regionFilters ? this.state.regionFilters.inspection : null;

            let accuracyTrendChartData: AccuracyTrendChartItem[] = [];
            let adequacyTrendChartData: AdequacyTrendChartItem[] = [];
            if (currentModule !== 'Mapping') {
              accuracyTrendChartData = await this.state.runConfiguration.getAccuracyTrendChartData(this.state.runFrequency, regionSetQualifier, analysisSpecialtyGroup, inspection);
              adequacyTrendChartData = await this.state.runConfiguration.getAdequacyTrendChartData(this.state.runFrequency, regionSetQualifier, analysisSpecialtyGroup);
            }

            let adequacyTrendChartDataDualMembership: AdequacyTrendChartItem[] = [];
            if ([ProjectRunConfigurationType.DualMembershipPrimary, ProjectRunConfigurationType.DualMembershipSupplemental].includes(this.state.runConfiguration.projectRunConfigurationType) && !this.state.project.isCompanionRunning && this.state.compareToRunConfiguration) {
              adequacyTrendChartDataDualMembership =
                await this.state.compareToRunConfiguration.getAdequacyTrendChartData(this.state.runFrequency, regionSetQualifier, analysisSpecialtyGroup);
            }
            // in the event the compared run configuration has data in timeslices that don't have selected configuration data...
            let comparedAdequacyTrendChartData: AdequacyTrendChartItem[] = [];
            let malleableAdequacyTrendChartData = adequacyTrendChartData || [];
            if (this.state.compareToRunConfiguration && this.state.compareToAdequacyResult.aggregateSummary) {
              comparedAdequacyTrendChartData = await this.state.compareToRunConfiguration.getAdequacyTrendChartData(this.state.runFrequency, this.state.compareToAdequacyResult.aggregateSummary, analysisSpecialtyGroup);
            }

            // index 0 is the most recent run, published or not
            const configurationsToShow = this.state.runFrequency !== TimeFrequency.Monthly ? this.state.project.projectRunConfigurations.filter((prc, index) => (!!prc.datePublished || index === 0) && prc.projectRunConfigurationType === runConfiguration.projectRunConfigurationType && !(prc.isTermed || prc.isProjected || prc.isProjectedTermed)) : [];
            const nonProjectedTimeslices: AdequacyTrendChartItem[] = [];
            const projectedTimeslices: AdequacyTrendChartItem[] = [];
            malleableAdequacyTrendChartData.forEach((timeslice) => {
              if (timeslice.runFrequency === this.state.runFrequency) {
                if (
                  timeslice.itemType === TrendChartItemType.Projected
                  || timeslice.itemType === TrendChartItemType.ProjectedTermed
                  || timeslice.itemType === TrendChartItemType.Termed
                ) {
                  projectedTimeslices.push(timeslice);
                } else {
                  nonProjectedTimeslices.push(timeslice);
                }
              }
            });
            // I think this is right - a dual membership project can't have separate timeslices or specialties/regions per membership
            if (configurationsToShow.length > nonProjectedTimeslices.length && !this.state.isDualMembershipMode) {
              // find the difference
              const missingConfigs =
                configurationsToShow.filter((config) =>
                  !nonProjectedTimeslices.find((item) => item.projectRunConfigurationId === config.projectRunConfigurationId) &&
                  (config.projectRunConfigurationId === runConfiguration.projectRunConfigurationId ||
                    (compareToRunConfiguration && config.projectRunConfigurationId === compareToRunConfiguration.projectRunConfigurationId)));
              // create fake trend chart items
              const fakeItems = missingConfigs.map<AdequacyTrendChartItem>((config) => ({
                accessOnlyMetCount: null,
                adequacyIndex: null,
                date: config.dateCreated,
                disabled: true,
                itemType: TrendChartItemType.OlderPublished,
                noneMetCount: 0,
                oneMetCount: null,
                projectRunConfigurationId: config.projectRunConfigurationId,
                publishComment: null,
                runFrequency: this.state.runFrequency,
                servicingOnlyMetCount: null,
              }));

              // insert accordingly
              malleableAdequacyTrendChartData = nonProjectedTimeslices.concat(fakeItems).sort((a, b) => a.projectRunConfigurationId > b.projectRunConfigurationId ? 1 : -1).concat(projectedTimeslices);
            }
            //}
            const combinedData = [...accuracyTrendChartData, ...malleableAdequacyTrendChartData, ...adequacyTrendChartDataDualMembership];

            if (compareToRunConfiguration && !this.state.isDualMembershipMode) {
              //don't want to set the compare to run configuration equal to the run configuration. If we can't find another match, then just take it out of compare mode.
              const trendItemRunConfigurationId = await this.mapSelectedTrendItem(combinedData || comparedAdequacyTrendChartData, runConfiguration.projectRunConfigurationId, moment(runConfiguration.dateCreated), false, true);
              const filteredCombinedData = combinedData.filter((cd) => cd.projectRunConfigurationId !== trendItemRunConfigurationId);
              await this.mapSelectedTrendItem(filteredCombinedData, compareToRunConfiguration.projectRunConfigurationId, moment(compareToRunConfiguration.dateCreated), this.state.isDualMembershipMode);
            } else {
              await this.mapSelectedTrendItem(combinedData || comparedAdequacyTrendChartData, runConfiguration.projectRunConfigurationId, moment(runConfiguration.dateCreated), false);
            }

            //If the value isn't equal then another result started loading after this one
            if (this.currentLoadingTrackerId === loadingTrackerId) {
              const spreadQuery = { ...this.props.query };
              //Only update state if no results have started loading after this one.
              this.setState((prevState): CountyProjectContainerState => ({
                ...prevState,
                ...accuracyResults,
                ...adequacyResults,
                ...attestationResults,
                ...compositeResults,
                accuracyFilters: spreadQuery,
                //don't show anything if there's nothing to show. Incorrect data would be displayed when viewing by inspection otherwise
                accuracyTrendChartData,
                adequacyFilters: isEqual(spreadQuery, prevState.adequacyFilters) ? prevState.adequacyFilters : spreadQuery, // prevent unnecessary updates
                adequacyTrendChartData: malleableAdequacyTrendChartData.length ? malleableAdequacyTrendChartData : prevState.adequacyTrendChartData,
                adequacyTrendChartDataDualMembership: adequacyTrendChartDataDualMembership && adequacyTrendChartDataDualMembership.length ? adequacyTrendChartDataDualMembership : prevState.adequacyTrendChartDataDualMembership,
                compositeFilters: spreadQuery,
                loadingResults: false,
              }));
            }
          });

        } catch (error) {
          console.error(error);
          this.setState({ error });
        }
      });
    }
  }

  // When the selected runConfigurationId and timestamp does not match one of the trend items, the selection is mapped to an approximate selection that matches a trend item
  // This can happen when switching from all runs to monthly
  private async mapSelectedTrendItem(trendChartData: TrendChartItemBase[], projectRunConfigurationId: number, runConfigurationTimestamp: moment.Moment, isCompareTo: boolean, suppressUpdate?: boolean): Promise<number> {
    const noTrendItemMatchesSelectedRun = trendChartData
      && trendChartData.length > 0
      && trendChartData.every((t) =>
        !(t.projectRunConfigurationId === projectRunConfigurationId
          || (!runConfigurationTimestamp
            || (t.date
              && runConfigurationTimestamp
              && moment.utc(t.date).isSame(runConfigurationTimestamp))
          )
        ),
      );

    if (noTrendItemMatchesSelectedRun) {
      const approximateTrendItem = trendChartData.find((t) => t.itemType === TrendChartItemType.LatestPublished)
      if (approximateTrendItem) {
        if (!suppressUpdate)
          await this.setRunConfiguration(approximateTrendItem.projectRunConfigurationId, moment.utc(approximateTrendItem.date), isCompareTo);
        return approximateTrendItem.projectRunConfigurationId;
      }
      if (isCompareTo) {
        //clear out the compare to run configuration if a matching one wasn't found
        await this.setRunConfiguration(undefined, undefined, true);
        return undefined;
      }
    }

    if (isCompareTo && (!trendChartData || trendChartData.length === 0)) {
      //clear out the compare to run configuration if a matching one wasn't found
      await this.setRunConfiguration(undefined, undefined, true);
      return undefined;
    }

    return projectRunConfigurationId;
  }

  /** Attempts to update the state given the props for the Container. */
  private async updateState(updateCachedRunConfiguration?: boolean) {
    this.setState({ error: null, loading: true, loadingResults: true }, async () => {
      try {
        const networkAssessmentAnalysisType = this.props.query.opportunity ? NetworkAssessmentAnalysisType.Opportunity : NetworkAssessmentAnalysisType.ServiceArea;
        const projectRunConfigurationType = this.props.query.projectRunConfigurationType ? this.props.query.projectRunConfigurationType : ProjectRunConfigurationType.DualMembershipPrimary;
        let projectDetails: Project = this.state.project;
        if (!this.props.location.pathname.includes("mapping") || !projectDetails) {
          projectDetails = await this.projectService.getById(this.props.projectId, true);
        }

        let templateMetadata: TemplateMetadata = null;

        if (projectDetails.adequacyTemplateCollectionId && projectDetails.templateId) {
          templateMetadata = await this.templateService.getTemplateById(projectDetails.adequacyTemplateCollectionId, projectDetails.templateId, false);
        }

        const project = projectDetails as Project;
        project.hasUnprocessedSpecialtyGroupExceptions = project.projectRunConfigurations?.some(r => (r.isLatestPublished || r.datePublished === null) && r.hasUnprocessedSpecialtyGroupExceptions) ?? false;

        if (!this.state.runConfiguration) {
          const latestPublished: ProjectRunConfiguration = project.projectRunConfigurations?.find((rc) =>
            rc.isLatestPublished && !rc.isProjected && !rc.isTermed && !rc.isProjectedTermed);

          // When navigating to the mapping page, use the latest published time slice.
          if (this.props.location.pathname.includes("mapping") || this.props.location.pathname.includes("provider-hub")) {
            this.runConfigurationId = latestPublished?.projectRunConfigurationId || undefined;
          }
          else {
            let rci;
            // In the event that we do not have a run configuration (initial load) do a search in the query parameters to see if one is specified.
            const queryShape = parseQuerystring(this.props.location.search) as { projectRunConfigurationId?: string };
            const queryRunConfigurationId = Number(queryShape.projectRunConfigurationId) || null;
            const localStorageKey = getLocalStorageRunConfigurationKey(project.projectId);
            // leave it as a string for now - we have to null check it before typing
            const localStorageRunConfigurationDataString = localStorage.getItem(localStorageKey);

            // Attempt to find this run configuration in the set of run configurations contained by the project.
            if (queryRunConfigurationId) {
              rci = project.projectRunConfigurations && project.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === queryRunConfigurationId);
            }
            // Or if that doesn't work, check local storage
            else if (localStorageRunConfigurationDataString) {
              const data: LocalStorageRunConfigurationItem = JSON.parse(localStorageRunConfigurationDataString);
              rci = project.projectRunConfigurations?.find((rc) => rc.projectRunConfigurationId === data.projectRunConfigurationId);
              // but if there's been a new publish since this selection, we should use the latest published instead and remove the saved one
              // we also need to make sure this latest published isn't projected or terminated, since those are typically in the future

              const latestPublishedDate = convertDateTime(latestPublished.datePublished);
              if (latestPublishedDate.getTime() > (new Date(data.timeInserted)).getTime()) {
                rci = latestPublished;
                localStorage.removeItem(localStorageKey);
              }
            }

            this.runConfigurationId = rci?.projectRunConfigurationId || undefined;
            // AT THIS TIME we don't utilize the query string for the run configuration elsewhere. As such, clear this initial "load parameter" so as to not
            // generate a confusing URL when the run configuration is subsequently changed by the user.
            // Keeping the run configuration in the URL would make for an *EXCELLENT* future work item, though. Would really improve the ability to share links.
            delete queryShape.projectRunConfigurationId;
            this.props.pushOrReplaceLocation({ search: querystringify(queryShape) });
          }
        }
        else if (this.state.runConfiguration && this.state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental) {
          this.runConfigurationId = this.state.runConfiguration.companionProjectRunConfigurationId;
        }

        this.runConfigurationId = this.runConfigurationId || this.getDefultProjectRunConfigurationId(project, projectRunConfigurationType);

        let runConfiguration: ProjectRunConfigurationResult = null;
        if (!!this.runConfigurationId) {
          const runKey = keyFor(this.runConfigurationId, networkAssessmentAnalysisType);
          runConfiguration = this.runConfigurationMap[runKey];
          if (!runConfiguration || updateCachedRunConfiguration) {
            runConfiguration = await createRunConfigurationResult(
              this.runConfigurationId,
              project,
              this.projectService,
              this.specialtyGroupExceptionsService,
              this.accuracyService,
              this.attestationService,
              this.templateService,
              this.geographyService,
              networkAssessmentAnalysisType,
            );
          }

          if (runConfiguration && runConfiguration.status === ProjectRunConfigurationStatus.Succeeded) {
            this.runConfigurationMap[runKey] = runConfiguration;
          }
        }

        const routeState = (this.props.location.state || {}) as CountyProjectContainerState;
        let compareToRunConfigurationId: number;
        let supplementaryRunConfiguration: ProjectRunConfigurationResult;
        if ((routeState.initialDualMembershipMode || routeState.isDualMembershipMode) && (runConfiguration && runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipPrimary)) {
          compareToRunConfigurationId = runConfiguration.companionProjectRunConfigurationId;
          supplementaryRunConfiguration = await createRunConfigurationResult(
            compareToRunConfigurationId,
            project,
            this.projectService,
            this.specialtyGroupExceptionsService,
            this.accuracyService,
            this.attestationService,
            this.templateService,
            this.geographyService,
            networkAssessmentAnalysisType,
          );
        }

        // Intentionally not setting loadingResults to false here as the results still need to be loaded
        // Result loading will be automatically triggered via the component did update
        this.setState({
          accuracyResult: { aggregateDetails: [], aggregateSummary: null, availableRegionSetStatusTypes: [], details: [] },
          adequacyResult: { aggregateDetails: [], aggregateSummary: null, availableRegionSetStatusTypes: [], details: [] },
          attestationResult: { aggregateDetails: [], aggregateSummary: null, details: [] },
          compareToAccuracyResult: { aggregateDetails: [], aggregateSummary: null, availableRegionSetStatusTypes: [], details: [] },
          compareToAdequacyResult: { aggregateDetails: [], aggregateSummary: null, availableRegionSetStatusTypes: [], details: [] },
          compareToAttestationResult: { aggregateDetails: [], aggregateSummary: null, details: [] },
          compareToCompositeResult: undefined,
          compareToRunConfiguration: supplementaryRunConfiguration,
          compareToRunConfigurationTimestamp: supplementaryRunConfiguration ? moment(supplementaryRunConfiguration.dateCreated) : undefined,
          compositeResult: { aggregateDetails: [], aggregateSummary: null, details: [] },
          error: null,
          hasProspectOrMarketProviders: true,
          initialDualMembershipMode: routeState.initialDualMembershipMode,
          isDualMembershipMode: (routeState.initialDualMembershipMode || routeState.isDualMembershipMode) && !!supplementaryRunConfiguration,
          loading: false,
          networkAssessmentAnalysisType,
          project,
          runConfiguration,
          templateMetadata,
        }, () => {
          this.doesProjectHaveImpactProviders().then((response) => {
            this.setState({
              hasProspectOrMarketProviders: response,
            })
          });
        });
      } catch (error) {
        datadogRum.addError(`Error loading project: ${error}`);
        this.setState({ error });
      }
    });
  }

  private getDefultProjectRunConfigurationId(project: Project, projectRunConfigurationType: ProjectRunConfigurationType) {
    if (this.props.user.roles.indexOf(Roles.PREVIEW) > -1) {
      return this.projectService.maxRunConfigurationId(project, projectRunConfigurationType);
    }

    return this.projectService.maxLastPublishedRunConfigurationId(project, projectRunConfigurationType);
  }

  private async updateStateForProviderChanges(projectionProcessed: boolean) {
    this.setState({ error: null, loading: true, loadingResults: true }, async () => {
      try {
        // get a new collection of the project run configurations
        // by getting the project from the API
        const updatedProject = (await this.projectService.getById(this.props.projectId, true)) as Project;
        let runConfiguration = !!updatedProject && !!updatedProject.projectRunConfigurations
          ? updatedProject.projectRunConfigurations.find((prc) => projectionProcessed ? prc.isProjected : prc.isTermed)
          : undefined;

        if (!runConfiguration) {
          // if we don't have a termed run config, we assume we've removed all projected and terminated providers and need to
          // use the latest run config as the selected run config
          runConfiguration = !!updatedProject && !!updatedProject.projectRunConfigurations
            ? updatedProject.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === this.projectService.maxRunConfigurationId(updatedProject, ProjectRunConfigurationType.DualMembershipPrimary))
            : undefined;
        }

        if (runConfiguration) {
          // clone the current project and replace it's projectRunConfigurations...doing this
          // explicitly here for readability of 'why' we're resetting the project in the state
          const project = { ...this.state.project };
          project.projectRunConfigurations = [...updatedProject.projectRunConfigurations];
          project.isRunning = updatedProject.isRunning; //Necessary because we modified this to true when termination processing was run.
          project.hasUnprocessedProjectionProviders = false; //both are run at the same time
          project.hasUnprocessedProviderTerminations = false;

          this.setState((prevState) => ({ ...prevState, project }), async () => {
            if (runConfiguration.isTermed || runConfiguration.isProjectedTermed) {
              this.runConfigurationMap[keyFor(runConfiguration.projectRunConfigurationId, this.state.networkAssessmentAnalysisType)] = undefined;
            }
            let newPartialState: Partial<CountyProjectContainerState> = {};
            // We account for primary and secondary membership selections here, but don't care about dual membership mode because you can't make provider changes in it
            if (this.state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental) {
              newPartialState = await this.setRunConfiguration(runConfiguration.companionProjectRunConfigurationId, undefined, false, undefined, true);
            } else {
              newPartialState = await this.setRunConfiguration(runConfiguration.projectRunConfigurationId, undefined, undefined, undefined, true);
            }
            const newState = {
              ...newPartialState,
              loading: false,
              loadingResults: false,
            };
            this.updateRunConfigurationState(newState);
          });
        } else {
          //Something happened no error, but can't find a termed run or latest run config
          this.setState({ loadingResults: false });
        }
      } catch (error) {
        this.setState({ error });
      }
    });
  }

  private async updateStateForNextClosestProviders() {
    this.setState({ error: null, hasProspectOrMarketProviders: true, loading: true, loadingResults: true }, async () => {
      try {
        // get a new collection of the project run configurations
        // by getting the project from the API
        const updatedProject = (await this.projectService.getById(this.props.projectId, true)) as Project;
        const runConfiguration = !!updatedProject && !!updatedProject.projectRunConfigurations
          ? updatedProject.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === this.projectService.maxRunConfigurationId(updatedProject, ProjectRunConfigurationType.DualMembershipPrimary))
          : undefined;

        if (runConfiguration) {
          // clone the current project and replace it's projectRunConfigurations...doing this
          // explicitly here for readability of 'why' we're resetting the project in the state
          const project = cloneDeep(this.state.project);
          project.projectRunConfigurations = [...updatedProject.projectRunConfigurations];
          project.isEditRestricted = updatedProject.isEditRestricted;
          project.isRunning = updatedProject.isRunning; //Necessary because we modified this to true when termination processing was run.
          project.hasUnprocessedProjectionProviders = false; //both are run at the same time
          project.hasUnprocessedProviderTerminations = false;
          project.isCompanionRunning = false; // the notification isn't sent until both are finished
          const hasImpact = await this.doesProjectHaveImpactProviders();

          this.setState((prevState) => ({ ...prevState, hasProspectOrMarketProviders: hasImpact, project }), async () => {
            let newPartialState: Partial<CountyProjectContainerState> = {};
            // We account for primary and secondary membership selections here, but don't care about dual membership mode because you can't make provider changes in it
            if (this.state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental) {
              newPartialState = await this.setRunConfiguration(runConfiguration.companionProjectRunConfigurationId, undefined, false, undefined, true);
            } else {
              newPartialState = await this.setRunConfiguration(runConfiguration.projectRunConfigurationId, undefined, undefined, undefined, true);
            }
            const newState = {
              ...newPartialState,
              loading: false,
              loadingResults: false,
            };
            await this.updateRunConfigurationState(newState);
            await this.setDualMembershipMode(this.state.runConfiguration &&
              this.state.compareToRunConfiguration &&
              this.state.runConfiguration.companionProjectRunConfigurationId === this.state.compareToRunConfiguration.projectRunConfigurationId
            );
          });
        } else {
          //Something happened no error, but can't find a termed run or latest run config
          this.setState({ loadingResults: false });
        }
      } catch (error) {
        this.setState({ error });
      }
    });
  }

  private handleAccuracyTemplateChange = (accuracyTemplate: AccuracyTemplate) => {
    this.setState({
      accuracyTemplate,
      loadedAccuracyTemplates: true,
    });
  }

  private doesProjectHaveImpactProviders = async () => {
    if (!this.state.project.showNextClosestNetworkProviders || !this.state.regionFilters.county || !this.state.regionFilters.specialty) {
      return true; // default value
    }

    const county = this.state.runConfiguration.getCounty(this.state.regionFilters.county);

    const hasImpactProviders = await this.projectService.projectHasImpactProviders(this.state.project.projectId, county?.regionSetId, this.state.runConfiguration.projectRunConfigurationId, this.state.regionFilters.specialty, this.state.runConfiguration.networkAssessmentAnalysisType);

    // if we don't have the information to send a request, we don't want to accidentally return "false" for a null
    if (hasImpactProviders === null) {
      return this.state.hasProspectOrMarketProviders;
    }

    return hasImpactProviders;
  }

  private setProjectReadOnlyProperties = (isReadOnly: boolean, isEditRestricted: boolean, delay?: boolean) => {
    if (this.state.project.isReadOnly !== isReadOnly || this.state.project.isEditRestricted !== isEditRestricted) {
      const projectClone = cloneDeep(this.state.project);
      projectClone.isReadOnly = isReadOnly;
      projectClone.isEditRestricted = isEditRestricted;
      if (!delay) {
        this.setState({
          project: projectClone,
        });
      }
      return projectClone;
    }
    return this.state.project;
  }

  private handleNotifications = async (message: Message) => {
    if (message) {
      const messageType = message.notificationType as MessageType;
      switch (messageType) {
        case MessageType.ProjectRunStatusChange:
          const projectRunStatusChangePayload = message.payload as ProjectRunStatusChangePayload;
          if (this.state.project &&
            this.state.project.projectId === projectRunStatusChangePayload.projectId) {
            this.setProjectReadOnlyProperties(projectRunStatusChangePayload.isReadOnly, projectRunStatusChangePayload.isEditRestricted);
            if ((!this.state.runConfiguration ||
              this.state.runConfiguration.status !== projectRunStatusChangePayload.status ||
              projectRunStatusChangePayload.status === ProjectRunConfigurationStatus.Succeeded)) {

              this.runConfigurationId = projectRunStatusChangePayload.projectRunConfigurationId;
              if (projectRunStatusChangePayload.status === ProjectRunConfigurationStatus.Succeeded) {
                if ((this.state.runConfiguration && this.state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental)
                  || this.state.isDualMembershipMode) {
                  await this.setDualMembershipMode(false, ProjectRunConfigurationType.DualMembershipPrimary, undefined, undefined, true);
                }
                await this.updateState(true);
              } else {
                await this.updateState();
              }

            }
          }
          break;
        case MessageType.ProcessingSpecialtyGroupExceptions:
          const processingSpecialtyGroupExceptionsPayload = message.payload as ProjectRunStatusChangePayload;
          if (this.state.project &&
            this.state.project.projectId === processingSpecialtyGroupExceptionsPayload.projectId &&
            (!this.state.project.resource.isShared || this.props.user.roles.indexOf(Roles.PREVIEW) > -1 || processingSpecialtyGroupExceptionsPayload.isPublished)) {
            this.setProjectReadOnlyProperties(processingSpecialtyGroupExceptionsPayload.isReadOnly, processingSpecialtyGroupExceptionsPayload.isEditRestricted);
            if (processingSpecialtyGroupExceptionsPayload.status === ProjectRunConfigurationStatus.Succeeded) {
              if (processingSpecialtyGroupExceptionsPayload.projectRunConfigurationId === this.projectService.maxRunConfigurationId(this.state.project, ProjectRunConfigurationType.DualMembershipPrimary) ||
                processingSpecialtyGroupExceptionsPayload.projectRunConfigurationId === this.projectService.maxRunConfigurationId(this.state.project, ProjectRunConfigurationType.DualMembershipSupplemental)) {
                //we always update the latest run configuration for re-aggregation and process low to high so only reload results once we get a notification for the latest run configuration

                if ((this.state.runConfiguration && this.state.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental)
                  || this.state.isDualMembershipMode) {
                  await this.setDualMembershipMode(false, ProjectRunConfigurationType.DualMembershipPrimary, undefined, undefined, true);
                }

                const updatedRunConfiguration = this.state.project.projectRunConfigurations.find((runConfiguration) => runConfiguration.projectRunConfigurationId === processingSpecialtyGroupExceptionsPayload.projectRunConfigurationId);

                //any later run configurations and the companion run configuration also need to be re-retrieved (this clears the lookup so they will refreshed be if the user switches to them)
                this.state.project.projectRunConfigurations
                  .filter((runConfiguration) => runConfiguration.projectRunConfigurationId >= processingSpecialtyGroupExceptionsPayload.projectRunConfigurationId
                    || (updatedRunConfiguration && updatedRunConfiguration.companionProjectRunConfigurationId === runConfiguration.projectRunConfigurationId))
                  .map((runConfiguration) => runConfiguration.projectRunConfigurationId)
                  .forEach((runConfigurationId) => this.runConfigurationMap[keyFor(runConfigurationId, this.state.networkAssessmentAnalysisType)] = undefined);

                await this.updateState(true);
              } else {
                //if it's not the latest run configuration, remove it from the map so it's reloaded next time the user asks for it
                this.runConfigurationMap[keyFor(processingSpecialtyGroupExceptionsPayload.projectRunConfigurationId, this.state.networkAssessmentAnalysisType)] = undefined;
              }
            } else if (processingSpecialtyGroupExceptionsPayload.status == ProjectRunConfigurationStatus.ProcessingSpecialtyGroupExceptionAnalysis) {
              try {
                const project = this.setProjectReadOnlyProperties(true, true, true);

                project.isRunning = true;
                let latestPublishedConfiguration = project.projectRunConfigurations.find((prc) => prc.isLatestPublished && !prc.isProjected && !prc.isTermed && !prc.isProjectedTermed);
                const dualMembershipTypes = [ProjectRunConfigurationType.DualMembershipPrimary, ProjectRunConfigurationType.DualMembershipSupplemental];
                // basically, if the secondary membership was selected, we want to keep the secondary membership selected
                if (
                  this.state.runConfiguration &&
                  dualMembershipTypes.includes(this.state.runConfiguration.projectRunConfigurationType) &&
                  dualMembershipTypes.includes(latestPublishedConfiguration.projectRunConfigurationType) &&
                  latestPublishedConfiguration.projectRunConfigurationType !== this.state.runConfiguration.projectRunConfigurationType
                ) {
                  latestPublishedConfiguration = project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === latestPublishedConfiguration.companionProjectRunConfigurationId);
                }
                let partialState: Partial<CountyProjectContainerState> = {
                  runConfiguration: cloneDeep(this.state.runConfiguration),
                };
                if (latestPublishedConfiguration.projectRunConfigurationId !== this.state.runConfiguration.projectRunConfigurationId) {
                  partialState = await this.setRunConfiguration(latestPublishedConfiguration.projectRunConfigurationId, undefined, false, undefined, true);
                }
                partialState.runConfiguration.status = ProjectRunConfigurationStatus.ProcessingSpecialtyGroupExceptionAnalysis;
                this.setState((prevState) => ({
                  ...prevState,
                  loadingResults: true,
                  project,
                  ...partialState,
                }));

              } catch (error) {
                this.setState({ error });
              }
            }
          }
          break;

        case MessageType.ProcessingProjectionAnalysis:
        case MessageType.ProcessingProviderChanges:
          const providerChangePayload = message.payload as ProjectRunStatusChangePayload;
          if (this.state.project &&
            this.state.project.projectId === providerChangePayload.projectId &&
            (!this.state.project.resource.isShared || this.props.user.roles.indexOf(Roles.PREVIEW) > -1 || providerChangePayload.isPublished)) {
            this.setProjectReadOnlyProperties(providerChangePayload.isReadOnly, providerChangePayload.isEditRestricted);
            if (providerChangePayload.status === ProjectRunConfigurationStatus.Succeeded) {
              if (providerChangePayload.projectRunConfigurationId === this.projectService.maxRunConfigurationId(this.state.project, ProjectRunConfigurationType.DualMembershipPrimary) ||
                providerChangePayload.projectRunConfigurationId === this.projectService.maxRunConfigurationId(this.state.project, ProjectRunConfigurationType.DualMembershipSupplemental)) {
                //we always update the latest run configuration for re-aggregation and process low to high so only reload results once we get a notification for the latest run configuration
                const updatedRunConfiguration = this.state.project.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === providerChangePayload.projectRunConfigurationId);

                //any later run configurations and the companion run configuration also need to be re-retrieved (this clears the lookup so they will refreshed be if the user switches to them)
                this.state.project.projectRunConfigurations
                  .filter((rc) => rc.projectRunConfigurationId >= providerChangePayload.projectRunConfigurationId
                    || (updatedRunConfiguration && updatedRunConfiguration.companionProjectRunConfigurationId === rc.projectRunConfigurationId))
                  .map((rc) => rc.projectRunConfigurationId)
                  .forEach((runConfigurationId) => this.runConfigurationMap[keyFor(runConfigurationId, this.state.networkAssessmentAnalysisType)] = undefined);
                const runConfiguration = this.runConfigurationMap[keyFor(providerChangePayload.projectRunConfigurationId, this.state.networkAssessmentAnalysisType)];
                if (runConfiguration) {
                  //just set this run configuration to succeeded. Nothing else changed. The projected run configuration will be reloaded.
                  runConfiguration.status = ProjectRunConfigurationStatus.Succeeded;
                }
                await this.updateStateForProviderChanges(messageType === MessageType.ProcessingProjectionAnalysis);

              } else {
                //if it's not the latest run configuration, remove it from the map so it's reloaded next time the user asks for it
                this.runConfigurationMap[keyFor(providerChangePayload.projectRunConfigurationId, this.state.networkAssessmentAnalysisType)] = undefined;
              }
            }
          }
          break;
        case MessageType.ProcessingProspectMarketNcp:
          const ncpPayload = message.payload as ProjectRunStatusChangePayload;
          if (this.state.project &&
            this.state.project.projectId === ncpPayload.projectId &&
            (!this.state.project.resource.isShared || this.props.user.roles.indexOf(Roles.PREVIEW) > -1 || ncpPayload.isPublished)) {
            this.setProjectReadOnlyProperties(ncpPayload.isReadOnly, ncpPayload.isEditRestricted);
            if (ncpPayload.status === ProjectRunConfigurationStatus.Succeeded) {
              if (ncpPayload.projectRunConfigurationId === this.projectService.maxRunConfigurationId(this.state.project, ProjectRunConfigurationType.DualMembershipPrimary) ||
                ncpPayload.projectRunConfigurationId === this.projectService.maxRunConfigurationId(this.state.project, ProjectRunConfigurationType.DualMembershipSupplemental)) {
                //we always update the latest run configuration for re-aggregation and process low to high so only reload results once we get a notification for the latest run configuration
                const updatedRunConfiguration = this.state.project.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === ncpPayload.projectRunConfigurationId);

                //any later run configurations and the companion run configuration also need to be re-retrieved (this clears the lookup so they will refreshed be if the user switches to them)
                this.state.project.projectRunConfigurations
                  .filter((rc) => rc.projectRunConfigurationId >= ncpPayload.projectRunConfigurationId
                    || (updatedRunConfiguration && updatedRunConfiguration.companionProjectRunConfigurationId === rc.projectRunConfigurationId))
                  .map((rc) => rc.projectRunConfigurationId)
                  .forEach((runConfigurationId) => this.runConfigurationMap[keyFor(runConfigurationId, this.state.networkAssessmentAnalysisType)] = undefined);
                const runConfiguration = this.runConfigurationMap[keyFor(ncpPayload.projectRunConfigurationId, this.state.networkAssessmentAnalysisType)];
                if (runConfiguration) {
                  //just set this run configuration to succeeded. Nothing else changed. The projected run configuration will be reloaded.
                  runConfiguration.status = ProjectRunConfigurationStatus.Succeeded;
                }
                await this.updateStateForNextClosestProviders();

              } else {
                //if it's not the latest run configuration, remove it from the map so it's reloaded next time the user asks for it
                this.runConfigurationMap[keyFor(ncpPayload.projectRunConfigurationId, this.state.networkAssessmentAnalysisType)] = undefined;
              }
            }
          }
          break;
        case MessageType.ReportComplete:
          //const reportcompletePayload = message.payload as ReportCompletePayload;
          // TODO - pop up a toast notification that the report is ready
          break;
        case MessageType.MultiProjectRunStatusChange:
          const multiProjectPayload = message.payload as MultiProjectRunStatusChangePayload;
          if (
            multiProjectPayload.customProjectIds.includes(this.state.project.projectId) &&
            (!this.state.project.resource.isShared ||
              this.props.user.roles.includes(Roles.PREVIEW) ||
              (!this.state.project.hasUnpublishedResults && multiProjectPayload.isPublished))
          ) {
            this.setProjectReadOnlyProperties(multiProjectPayload.readOnly, multiProjectPayload.readOnly);
            if (multiProjectPayload.status === ProjectRunConfigurationStatus.Succeeded || multiProjectPayload.status === ProjectRunConfigurationStatus.Failed) {
              await this.updateStateForProviderChanges(false);
            }
          }
          break;
        case MessageType.AccuracyGapAnalysisRunStatusChange:
          const accuracyGapAnalysisPayload = message.payload as AccuracyGapAnalysisStatusChangeNotificationMessagePayload;
          if (this.state.project && this.state.project.projectId == accuracyGapAnalysisPayload.projectId)
            await this.updateState(true);
          break;
        case MessageType.BulkTargetProjectRunStatusChange:
          const bulkTargetProjectRunStatusPayload = message.payload as BulkTargetProjectRunStatusChangeNotificationMessagePayload;
          if (this.state.project && this.state.project.projectId == bulkTargetProjectRunStatusPayload.projectId)
            await this.updateState(true);
          break;
        case MessageType.PciDashboardStatusChange:
          const pciDashboardPayload = message.payload as PciDashboardStatusChangeNotificationMessagePayload;
          if (this.state.project && this.state.project.projectId == pciDashboardPayload.projectId) {
            await this.updateState(true);
          }
          break;
      }
    }
  }

  private setProjectHasUnprocessedTerminations = (needsToProcess: boolean, returnPartial?: boolean) => {
    const partialProject: Partial<Project> = {
      hasUnprocessedProviderTerminations: needsToProcess,
    };
    if (returnPartial) {
      return partialProject;
    }
    const newProjectModel = {
      ...this.state.project,
      ...partialProject,
    };
    this.setState({
      project: newProjectModel,
    });
  }

  private setProjectHasUnprocessedProjectionProviders = (needsToProcess: boolean, returnPartial?: boolean) => {
    const partialProject: Partial<Project> = {
      hasUnprocessedProjectionProviders: needsToProcess,
    };
    if (returnPartial) {
      return partialProject;
    }
    const newProjectModel = {
      ...this.state.project,
      ...partialProject,
    };
    this.setState({
      project: newProjectModel,
    });
  }

  // setting these each one at a time tends to overwrite earlier ones due to setState calls being batched and async,
  // so we'll just set them together when necessary
  private setProjectProjectionFlags = (
    hasUnprocessedProviderTerminations: boolean,
    hasUnprocessedProjectionProviders: boolean,
  ) => {
    let partialProject: Partial<Project>;
    if (hasUnprocessedProviderTerminations !== undefined) {
      partialProject = {
        ...partialProject,
        hasUnprocessedProviderTerminations,
      };
    }
    if (hasUnprocessedProjectionProviders !== undefined) {
      partialProject = {
        ...partialProject,
        hasUnprocessedProjectionProviders,
      };
    }

    const newProject = {
      ...this.state.project,
      ...partialProject,
    };
    this.setState({
      project: newProject,
    });
  }

  private setShouldRefreshProspectProviders = (needsToRefresh: boolean) => {
    this.setState({
      shouldRefreshProspectProviders: needsToRefresh,
    });
  }

  private setShouldRefreshMarketProviders = (needsToRefresh: boolean) => {
    this.setState({
      shouldRefreshMarketProviders: needsToRefresh,
    });
  }

  private setShouldRefreshNetworkProviders = (needsToRefresh: boolean) => {
    this.setState({
      shouldRefreshNetworkProviders: needsToRefresh,
    });
  }

  private setShouldRefreshNetNewProviders = (needsToRefresh: boolean) => {
    this.setState({
      shouldRefreshNetNewProviders: needsToRefresh,
    });
  }

  private setCMSCreditIsApplying = (active: boolean) => {
    this.setState({
      cmsCreditIsApplying: active,
    });
  }

  private setCMSCreditShowOnlyChanges = (active: boolean) => {
    this.setState({
      cmsCreditShowOnlyChanges: active,
    });
  }

  private setAttestationRateDays = (rateDays: AttestationRateDays) => {
    this.setState({
      attestationRateDays: rateDays,
    });
  }

  private clearCachedImpactAnalysisRunConfigurations = () => {
    const relevantRunConfigurations = this.state.project.projectRunConfigurations.filter((prc) =>
      prc.isProjected ||
      prc.isTermed ||
      prc.isProjectedTermed ||
      prc.isLatestPublished
    );
    relevantRunConfigurations.forEach((rrc) => {
      // We do have separate networkAssessmentAnalysisTypes for projected, termed, and projected-termed
      // We just evidently don't use those for caching run configurations in project container
      const rcKey = keyFor(rrc.projectRunConfigurationId, this.state.networkAssessmentAnalysisType);
      delete this.runConfigurationMap[rcKey];
      // this will re-request the current run configuration data from the API,
      // and is less disruptive than updateState()
      this.setRunConfiguration(this.state.runConfiguration.projectRunConfigurationId);
    })
  }

  // This function allows a partial update of the run configuration comments currently stored in the UI in lieu of a complete
  // refresh of the project
  private updateProjectRunConfigurationComment(comment: string, projectRunConfigurationId: number) {
    const project = { ...this.state.project };
    project.projectRunConfigurations = [...this.state.project.projectRunConfigurations];
    const runConfiguration = project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === projectRunConfigurationId);
    if (!runConfiguration) {
      return;
    }
    runConfiguration.publishComment = comment;
    const secondaryRunConfiguration = project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === runConfiguration.companionProjectRunConfigurationId);
    if (secondaryRunConfiguration) {
      secondaryRunConfiguration.publishComment = comment;
    }
    // Need to also update the comment on termed/projected slices if we're editing the most recent published slice
    if (runConfiguration.isLatestPublished) {
      for (const otherLatestPublishedRunConfigurations of project.projectRunConfigurations.filter(prc => prc.isLatestPublished)) {
        otherLatestPublishedRunConfigurations.publishComment = comment;
      }
    }
    this.setState({ project });
  }
}

const CountyWrappedProjectContainer =
  withQuery(withRouter(withUserContext(withNotificationContext(withCleanHistory(withFeatureFlagContext(CountyProjectContainer))))), (q) => {
    const all = q.all === undefined || q.all === "true";
    const exceptions = q.exceptions === "true";
    const opportunity = q.opportunity === "true";
    const gaps = q.gaps === "true";
    const active = q.active === true.toString();
    const approved = q.approved === true.toString();
    const pending = q.pending === true.toString();
    const changedGaps = q.changedGaps as ChangedGapsEnum || null;
    const viewBy = q.viewBy as ProjectPerspective || null;
    const specialtyType = q.specialtyType as SpecialtyTypeEnum || null;

    let projectRunConfigurationType: ProjectRunConfigurationType = null;
    if (q.projectRunConfigurationType) {
      projectRunConfigurationType = q.projectRunConfigurationType as ProjectRunConfigurationType;
    }

    return {
      active,
      all,
      approved,
      changedGaps,
      exceptions,
      gaps,
      opportunity,
      pending,
      projectRunConfigurationType,
      specialtyType,
      viewBy,
    };
  });

export default CountyWrappedProjectContainer as React.ComponentClass<CountyProjectContainerProps>;
export { AdequacyQueryShape };

/*********************************************************
 *                       HELPERS                         *
 *********************************************************/

/**
 * Retrieves the updated adequacy results for both current and comparison (if provided) run configurations.
 * NOTE: Wrap in try catch if necessary, this function will always allow exceptions to be thrown
 * @param runConfiguration
 * @param compareToRunConfiguration
 * @param perspective
 * @param regionFilters
 * @param adequacyAggregateFilters
 */
async function getUpdatedAdequacyResults(
  runConfiguration: ProjectRunConfigurationResult,
  compareToRunConfiguration: ProjectRunConfigurationResult | undefined,
  perspective: ProjectPerspective,
  regionFilters: RegionFilters,
  adequacyFilters: AdequacyFilters,
  cmsCreditState: boolean,
  cmsCreditShowOnlyChanges: boolean,
  currentModule: string
): Promise<{
  adequacyResult: AdequacyResult,
  compareToAdequacyResult?: AdequacyResult,
}> {
  let adequacyResult = await getUpdatedAdequacyResultsHelper(runConfiguration, perspective, regionFilters, adequacyFilters, currentModule);
  if (!adequacyResult || !adequacyResult.aggregateDetails || !adequacyResult.details) {
    adequacyResult = null;
  }

  let compareToAdequacyResult = await getUpdatedAdequacyResultsHelper(compareToRunConfiguration, perspective, regionFilters, adequacyFilters, currentModule);
  compareToAdequacyResult = {
    ...compareToAdequacyResult,
    adequacyResultTypeText: compareToAdequacyResult && compareToAdequacyResult.adequacyResultTypeText || undefined,
    aggregateDetails: compareToAdequacyResult && compareToAdequacyResult.aggregateDetails || undefined,
    aggregateSummary: compareToAdequacyResult && compareToAdequacyResult.aggregateSummary || undefined,
    availableRegionSetStatusTypes: compareToAdequacyResult && compareToAdequacyResult.availableRegionSetStatusTypes || undefined,
    details: compareToAdequacyResult && compareToAdequacyResult.details || undefined,
  };

  if (regionFilters.county && regionFilters.specialty) {
    //don't apply filtering once the user is already viewing a region-set-specialty-group summary result
    return { adequacyResult, compareToAdequacyResult };
  } else {
    return filterUpdatedAdequacyResults(adequacyResult, compareToAdequacyResult, adequacyFilters, cmsCreditState, cmsCreditShowOnlyChanges);
  }
}

const filterUpdatedAdequacyResults = (adequacyResult: AdequacyResult, compareToAdequacyResult: AdequacyResult, adequacyFilters: AdequacyFilters, cmsCreditIsApplying: boolean, cmsCreditShowOnlyChanges: boolean) => {
  const nullcheck = (adeqResult: AdequacyResult, child: keyof AdequacyResult, key: string) => {
    return adeqResult &&
      ((adeqResult)[child] as (AdequacyDetail | AdequacyAggregate)[]) &&
      ((adeqResult)[child] as (AdequacyDetail | AdequacyAggregate)[]).find((r) => r.key === key);
  };
  if (adequacyFilters.exceptions) {
    adequacyResult.aggregateDetails = adequacyResult.aggregateDetails.filter((d) => d.exceptionCount > 0 ||
      (nullcheck(compareToAdequacyResult, "aggregateDetails", d.key) &&
        compareToAdequacyResult.aggregateDetails.find((ad) => ad.key === d.key).exceptionCount > 0
      ),
    );
    adequacyResult.details = adequacyResult.details.filter((d) => {
      const foundResult = nullcheck(compareToAdequacyResult, "details", d.key) ? compareToAdequacyResult.details.find((ad) => ad.key === d.key) : null;
      return d.exception && d.exception.specialtyGroupExceptionStatus !== SpecialtyGroupExceptionStatus.Archived ||
        (foundResult &&
          foundResult.exception &&
          foundResult.exception.specialtyGroupExceptionStatus !== SpecialtyGroupExceptionStatus.Archived);
    });

    if (compareToAdequacyResult) {
      compareToAdequacyResult.aggregateDetails = compareToAdequacyResult.aggregateDetails.filter((d) => {
        const foundResult = !!adequacyResult.aggregateDetails ? adequacyResult.aggregateDetails.find((ad) => ad.key === d.key) : null;
        return d.exceptionCount > 0 ||
          (foundResult &&
            foundResult.exceptionCount > 0);
      });

      compareToAdequacyResult.details = compareToAdequacyResult.details.filter((d) => {
        const foundResult = !!adequacyResult.details ? adequacyResult.details.find((ad) => ad.key === d.key) : null;
        return d.exception && d.exception.specialtyGroupExceptionStatus !== SpecialtyGroupExceptionStatus.Archived ||
          (foundResult &&
            foundResult.exception &&
            foundResult.exception.specialtyGroupExceptionStatus !== SpecialtyGroupExceptionStatus.Archived);
      });
    }
  } else if (adequacyFilters.gaps) {
    adequacyResult.aggregateDetails = adequacyResult.aggregateDetails.filter((d) => (d.gapCount > 0 && !cmsCreditIsApplying && !cmsCreditShowOnlyChanges) ||
      (cmsCreditIsApplying && d.gapCountCMSCredit > 0) ||
      (cmsCreditShowOnlyChanges && d.gapCount !== d.gapCountCMSCredit) ||
      (nullcheck(compareToAdequacyResult, "aggregateDetails", d.key) &&
        compareToAdequacyResult.aggregateDetails.find((ad) => ad.key === d.key).gapCount > 0
      ),
    );
    adequacyResult.details = adequacyResult.details.filter((d) => {
      const foundResult = nullcheck(compareToAdequacyResult, "details", d.key) ? compareToAdequacyResult.details.find((ad) => ad.key === d.key) : null;

      return (!d.metCriteria && ((cmsCreditIsApplying && !d.applicationCreditMetCriteria) || !cmsCreditIsApplying)) || (foundResult && !foundResult.metCriteria);
    });

    if (compareToAdequacyResult) {
      compareToAdequacyResult.aggregateDetails = compareToAdequacyResult.aggregateDetails.filter(
        (d) => {
          const foundResult = !!adequacyResult.aggregateDetails ? adequacyResult.aggregateDetails.find((ad) => ad.key === d.key) : null;
          return d.gapCount > 0 ||
            (foundResult &&
              foundResult.gapCount > 0
            );
        },
      );
      compareToAdequacyResult.details = compareToAdequacyResult.details.filter(
        (d) => {
          const foundResult = !!adequacyResult.details ? adequacyResult.details.find((ad) => ad.key === d.key) : null;
          return !d.metCriteria || (foundResult && !foundResult.metCriteria);
        });
    }
  }
  if (cmsCreditIsApplying && cmsCreditShowOnlyChanges) {
    adequacyResult.aggregateDetails = adequacyResult.aggregateDetails.filter((d) => {
      return d.gapCountCMSCredit !== d.gapCount;
    });
    adequacyResult.details = adequacyResult.details.filter((d) => {
      return !d.metCriteria && d.applicationCreditMetCriteria;
    });
  }
  if (adequacyFilters.specialtyType) {
    if (adequacyFilters.specialtyType === SpecialtyTypeEnum.Informational) {
      adequacyResult.aggregateDetails = adequacyResult.aggregateDetails.filter(d => d.specialtyGroupIsInformational);
      adequacyResult.details = adequacyResult.details.filter(d => d.specialtyGroupIsInformational);
      if (compareToAdequacyResult) {
        compareToAdequacyResult.aggregateDetails = compareToAdequacyResult.aggregateDetails.filter(d => d.specialtyGroupIsInformational);
        compareToAdequacyResult.details = compareToAdequacyResult.details.filter(d => d.specialtyGroupIsInformational);
      }
    }
    if (adequacyFilters.specialtyType === SpecialtyTypeEnum.Required) {
      adequacyResult.aggregateDetails = adequacyResult.aggregateDetails.filter(d => !d.specialtyGroupIsInformational);
      adequacyResult.details = adequacyResult.details.filter(d => !d.specialtyGroupIsInformational);
      if (compareToAdequacyResult) {
        compareToAdequacyResult.aggregateDetails = compareToAdequacyResult.aggregateDetails.filter(d => !d.specialtyGroupIsInformational);
        compareToAdequacyResult.details = compareToAdequacyResult.details.filter(d => !d.specialtyGroupIsInformational);
      }
    }
  }

  return { adequacyResult, compareToAdequacyResult };
};

/**
 * All the logic for properly retrieving adequacy-results for a given run-configuration
 * NOTE: Wrap in try catch if necessary, this function will always allow exceptions to be thrown
 * @param runConfiguration
 * @param perspective
 * @param filters
 * @param adequacyAggregateFilters
 */
async function getUpdatedAdequacyResultsHelper(
  runConfiguration: ProjectRunConfigurationResult,
  perspective: ProjectPerspective,
  regionFilters: RegionFilters,
  adequacyFilters: AdequacyFilters,
  currentModule: string
): Promise<AdequacyResult> {
  const adequacyResult: AdequacyResult = { ...defaultAdequacyResult };

  if (!runConfiguration || !perspective || !runConfiguration.adequacyTemplateId || (currentModule !== "Adequacy" && currentModule !== "Mapping")) {
    return adequacyResult;
  }

  let countyRegionCode: string = null;
  let specialtyGroupCode: string = null;
  let stateRegionCode: string = null;
  let summaryResultRegion: ResultRegion = null;

  if (regionFilters) {
    countyRegionCode = regionFilters.county;
    specialtyGroupCode = regionFilters.specialty ? decodeURIComponent(regionFilters.specialty) : regionFilters.specialty;
    stateRegionCode = regionFilters.state;
  }

  if ((perspective === ProjectPerspective.ByCounty || perspective === ProjectPerspective.ByState) && !specialtyGroupCode) {
    if (!stateRegionCode && !countyRegionCode) {
      let resultRegions = runConfiguration.states();

      const parentRegions = new Map<string, StateName>();
      if (perspective === ProjectPerspective.ByCounty) {
        const regions = new Set<ResultRegion>();
        resultRegions.forEach((region) => {
          if (region.regionGranularity === RegionGranularity.County) {
            regions.add(region);
          } else if (region.regionGranularity === RegionGranularity.State) {
            region.subRegions.forEach((subRegion) => {
              if (subRegion.regionGranularity === RegionGranularity.County) {
                parentRegions.set(subRegion.regionCode, { abbreviation: region.regionCode, displayName: region.displayName });
                regions.add(subRegion);
              }
            });
          }
        });
        resultRegions = Array.from(regions);
      }

      const adequacyAggregateDetails = (await runConfiguration.getRegionSetAdequacyAggregates(resultRegions)).filter((v) => !!v);
      const adequacyAggregateSummary = await runConfiguration.getRegionSetAdequacyAggregate(runConfiguration.serviceArea);

      summaryResultRegion = runConfiguration.serviceArea;
      adequacyAggregateDetails.forEach((detail) => {
        if (parentRegions.has(detail.regionCode)) {
          const parentInfo = parentRegions.get(detail.regionCode);
          detail.parentAbbreviation = parentInfo.abbreviation;
          detail.parentDisplayName = parentInfo.displayName;
        }
      });

      adequacyResult.aggregateDetails = adequacyAggregateDetails;
      adequacyResult.aggregateSummary = adequacyAggregateSummary;
      adequacyResult.adequacyResultTypeText = "State";
    } else if (stateRegionCode && !countyRegionCode) {
      const resultRegion = runConfiguration.getState(stateRegionCode);
      summaryResultRegion = resultRegion;

      const adequacyAggregateSummary = await runConfiguration.getRegionSetAdequacyAggregate(resultRegion);
      //if the aggregate summary is null then the state doesn't exist on this run configuration
      const adequacyAggregateDetails = adequacyAggregateSummary ? (await runConfiguration.getRegionSetAdequacyAggregates(resultRegion.subRegions)).filter((v) => !!v) : null;

      adequacyResult.aggregateDetails = adequacyAggregateDetails;
      adequacyResult.aggregateSummary = adequacyAggregateSummary;
      adequacyResult.adequacyResultTypeText = "County";
    } else if (countyRegionCode) {
      const resultRegion = runConfiguration.getCounty(countyRegionCode);
      const adequacyAggregateSummary = await runConfiguration.getRegionSetAdequacyAggregate(resultRegion);
      //if the aggregate summary is null then the county doesn't exist on this run configuration
      const adequacyDetails = adequacyAggregateSummary ? await runConfiguration.getAdequacyDetails(resultRegion, undefined, "specialty") : null;

      adequacyResult.details = adequacyDetails;
      adequacyResult.aggregateSummary = adequacyAggregateSummary;
      adequacyResult.adequacyResultTypeText = "Specialty Group";
    }
  } else if (perspective === ProjectPerspective.BySpecialty && !specialtyGroupCode) {
    if (!stateRegionCode && !countyRegionCode) {
      const resultRegion = runConfiguration.serviceArea;
      summaryResultRegion = resultRegion;

      const adequacyAggregateDetails = await runConfiguration.getRegionSetSpecialtyGroupAdequacyAggregates(resultRegion);
      const adequacyAggregateSummary = await runConfiguration.getRegionSetAdequacyAggregate(resultRegion);

      adequacyResult.aggregateDetails = adequacyAggregateDetails.filter((v) => !!v);
      adequacyResult.aggregateSummary = adequacyAggregateSummary;
      adequacyResult.adequacyResultTypeText = "Specialty Group";
    } else if (stateRegionCode && !countyRegionCode) {
      const resultRegion = runConfiguration.getState(stateRegionCode);
      summaryResultRegion = resultRegion;

      const adequacyAggregateSummary = await runConfiguration.getRegionSetAdequacyAggregate(resultRegion);
      //if the aggregate summary is null then the state doesn't exist on this run configuration
      const adequacyAggregateDetails = adequacyAggregateSummary ? await runConfiguration.getRegionSetSpecialtyGroupAdequacyAggregates(resultRegion) : null;

      adequacyResult.aggregateDetails = adequacyAggregateDetails;
      adequacyResult.aggregateSummary = adequacyAggregateSummary;
      adequacyResult.adequacyResultTypeText = "Specialty Group";
    } else if (countyRegionCode) {
      const resultRegion = runConfiguration.getCounty(countyRegionCode);
      summaryResultRegion = resultRegion;
      const adequacyAggregateSummary = await runConfiguration.getRegionSetAdequacyAggregate(resultRegion);
      const adequacyDetails = await runConfiguration.getAdequacyDetails(resultRegion, undefined, "specialty");

      adequacyResult.details = adequacyDetails;
      adequacyResult.aggregateSummary = adequacyAggregateSummary;
      adequacyResult.adequacyResultTypeText = "Specialty Group";
    }
  } else if (perspective !== ProjectPerspective.All && specialtyGroupCode) {
    const specialtyGroup = runConfiguration.specialtyGroups.find((sg) => sg.code === specialtyGroupCode);
    if (specialtyGroup) {
      if (!stateRegionCode && !countyRegionCode) {
        let resultRegions: ResultRegion[] = [];
        const states = runConfiguration.states();
        if (perspective === ProjectPerspective.ByState) {
          resultRegions = states;
          adequacyResult.adequacyResultTypeText = "State";
        } else if (perspective === ProjectPerspective.ByCounty) {
          // prevent runConfiguration.counties() from returning duplicates
          resultRegions = runConfiguration.counties().filter((county, index, self) => {
            return index === self.findIndex((c) => c.regionCode === county.regionCode);
          });

          adequacyResult.adequacyResultTypeText = "County";
        }
        const adequacyAggregateDetails = (await runConfiguration.getBulkRegionSetSpecialtyGroupAdequacyAggregates(resultRegions, specialtyGroup)).filter((v) => !!v);
        const adequacyAggregateSummary = await runConfiguration.getRegionSetSpecialtyGroupAdequacyAggregate(runConfiguration.serviceArea, specialtyGroup);
        summaryResultRegion = runConfiguration.serviceArea;

        if (perspective === ProjectPerspective.ByCounty) {
          adequacyAggregateDetails.forEach((detail) => {
            const regionCode = detail.regionCode;
            const countyState = states.find((state) => !!state.subRegions.find((sr: ResultRegion) => sr.regionCode === regionCode));
            detail.parentAbbreviation = countyState.regionCode;
            detail.parentDisplayName = countyState.displayName;
          });
        }

        adequacyResult.aggregateDetails = adequacyAggregateDetails;
        adequacyResult.aggregateSummary = adequacyAggregateSummary;
      } else if (stateRegionCode && !countyRegionCode) {
        const resultRegion = runConfiguration.getState(stateRegionCode);
        summaryResultRegion = resultRegion;
        //if the state isn't on the run configuration then the summary and details should be null
        const adequacyAggregateSummary = resultRegion ? await runConfiguration.getRegionSetSpecialtyGroupAdequacyAggregate(resultRegion, specialtyGroup) : null;
        const adequacyDetails = resultRegion ? await runConfiguration.getAdequacyDetails(resultRegion, specialtyGroup) : null;

        adequacyResult.details = adequacyDetails;
        adequacyResult.aggregateSummary = adequacyAggregateSummary;
        adequacyResult.adequacyResultTypeText = "County";
      } else if (countyRegionCode) {
        const resultRegion = runConfiguration.getCounty(countyRegionCode);
        summaryResultRegion = resultRegion;
        //if the county isn't on the run configuration then the summary and details should be null
        const adequacyAggregateSummary = resultRegion ? await runConfiguration.getRegionSetSpecialtyGroupAdequacyAggregate(resultRegion, specialtyGroup) : null;
        const adequacyDetails = resultRegion ? await runConfiguration.getAdequacyDetails(resultRegion, specialtyGroup) : null;

        adequacyResult.details = adequacyDetails;
        adequacyResult.aggregateSummary = adequacyAggregateSummary;
        adequacyResult.adequacyResultTypeText = "Specialty Group";
      }
    } else {
      adequacyResult.aggregateDetails = [];
      adequacyResult.aggregateSummary = null;
      summaryResultRegion = runConfiguration.serviceArea;
      adequacyResult.details = [];
      adequacyResult.adequacyResultTypeText = countyRegionCode
        ? "Specialty Group"
        : stateRegionCode
          ? "County"
          : "State";
    }
  } else if (perspective === ProjectPerspective.All) {
    // still necessary here for the arc reactor and trend graph
    let resultRegions = runConfiguration.states();
    let specialtyGroups = runConfiguration.specialtyGroups;
    if (stateRegionCode) {
      const state = runConfiguration.getState(stateRegionCode);
      resultRegions = state ? [state] : [];
    }
    if (countyRegionCode) {
      const county = runConfiguration.getCounty(countyRegionCode);
      resultRegions = county ? [county] : [];
    }
    if (specialtyGroupCode) {
      const specialtyGroup = runConfiguration.specialtyGroups.find((sg) => sg.code === specialtyGroupCode);
      specialtyGroups = specialtyGroup ? [specialtyGroup] : [];
    }

    // Track the parents of regions so we can print state names in the table
    const parentRegions = new Map<string, StateName>();

    // We decided we always want the county-level granularity
    const regions = new Set<ResultRegion>();
    resultRegions.forEach((region) => {
      if (region.regionGranularity === RegionGranularity.County) {
        regions.add(region);
      } else if (region.regionGranularity === RegionGranularity.State) {
        region.subRegions.forEach((subRegion) => {
          if (subRegion.regionGranularity === RegionGranularity.County) {
            parentRegions.set(subRegion.regionCode, { abbreviation: region.regionCode, displayName: region.displayName });
            regions.add(subRegion);
          }
        });
      }
    });

    // Make a list of all the details we need for each region
    //TODO: THIS SHOULD BE DONE IN ONE API CALL
    //but for now the results are batched into 50 counties per call
    let detailPromises = new Array<Promise<AdequacyDetail[]>>();
    //if resultRegions or specialtyGroups are empty it's because the filter does not exist in this run configuration. This only happens in compare mode

    const regionArray = Array.from(regions);
    if (specialtyGroups.length === 1 && regionArray.length === 1) {
      //filtered by county and specialty group, just need to retrieve the single result
      detailPromises = resultRegions.length && specialtyGroups.length ? Array.from(regions).map(async (region) => await runConfiguration.getAdequacyDetails(region, specialtyGroups.length === 1 ? specialtyGroups[0] : null)) : [];
      //} else {
      // this "else" is handled in AdequacyDetailsMultiRegion now
    }

    // Get the details. When the data is returned from the Promises, condense all of it into a single array and return it
    const adequacyDetails = detailPromises.length
      ? await Promise.all(detailPromises).then((data) => {
        const condensedData = data.reduce((previousValue, currentValue) => {
          if (currentValue) {
            currentValue.forEach((value) => previousValue.push(value));
          }
          return previousValue;
        }, []);
        return condensedData;
      })
      : null;

    if (adequacyDetails) {
      adequacyDetails.forEach((detail) => {
        if (parentRegions.has(detail.regionCode)) {
          const parentInfo = parentRegions.get(detail.regionCode);
          detail.parentAbbreviation = parentInfo.abbreviation;
          detail.parentDisplayName = parentInfo.displayName;
        }
      });
    }

    let adequacyAggregateSummary: AdequacyAggregate;
    if (specialtyGroups.length === 1) {
      //if resultRegions or specialtyGroups are empty it's because the filter does not exist in this run configuration. This only happens in compare mode
      adequacyAggregateSummary = resultRegions.length && specialtyGroups.length ? await runConfiguration.getRegionSetSpecialtyGroupAdequacyAggregate(resultRegions.length === 1 ? resultRegions[0] : runConfiguration.serviceArea, specialtyGroups[0]) : null;
    } else {
      //if resultRegions or specialtyGroups are empty it's because the filter does not exist in this run configuration. This only happens in compare mode
      adequacyAggregateSummary = resultRegions.length && specialtyGroups.length ? await runConfiguration.getRegionSetAdequacyAggregate(resultRegions.length === 1 ? resultRegions[0] : runConfiguration.serviceArea) : null;
    }
    summaryResultRegion = resultRegions.length === 1 ? resultRegions[0] : runConfiguration.serviceArea;

    adequacyResult.details = adequacyDetails;
    adequacyResult.aggregateSummary = adequacyAggregateSummary;
    adequacyResult.adequacyResultTypeText = "Specialty Group";
  }

  // Now that we have the new results, apply our filter.
  let anyGapResults = false;
  let anyExceptionResults = false;
  let anyExpandableSpecialties = false;
  let anyResults = false;

  if (adequacyResult.aggregateDetails && adequacyResult.aggregateDetails.length > 0) {
    //determine if any of the iconToggle filters for All, Gaps, and Exceptions have to be enabled or disabled depending on data relevancy to populate.
    if (adequacyResult.aggregateDetails.some((d) => d.exceptionCount > 0)) {
      anyExceptionResults = true;
    }
    if (adequacyResult.aggregateDetails.some((d) => d.gapCount > 0)) {
      anyGapResults = true;
    }

    if (adequacyResult.aggregateDetails.length > 0) {
      anyResults = true;
    }

    if (adequacyResult.aggregateDetails.some(aggregate => aggregate.specialtyGroupIsExpandable)) {
      anyExpandableSpecialties = true;
    }

    if (perspective !== ProjectPerspective.BySpecialty && perspective !== ProjectPerspective.All) {
      adequacyResult.aggregateDetails = adequacyResult.aggregateDetails.filter((v) => {
        if (adequacyFilters.active === undefined && adequacyFilters.approved === undefined && adequacyFilters.pending === undefined && adequacyFilters.opportunity === undefined) {
          return true;
        }

        if (v.regionSetId === undefined) {
          return false;
        }

        const resultRegion = {
          regionCode: v.regionCode,
          regionGranularity: v.regionGranularity,
          regionSetId: v.regionSetId,
          subRegionCode: v.subRegionCode,
        } as ResultRegion;

        const availableRegionStatuses = runConfiguration.getAvailableRegionStatusTypes(resultRegion);

        if (!(adequacyFilters.active || adequacyFilters.approved || adequacyFilters.pending) &&
          !adequacyFilters.opportunity) {
          adequacyFilters.active = adequacyFilters.approved = adequacyFilters.pending = true;
        }

        let hasRegionStatus = false;

        if (availableRegionStatuses.indexOf(RegionSetStatusType.Active) > -1 || availableRegionStatuses.indexOf(RegionSetStatusType.Unknown) > -1) {
          hasRegionStatus = adequacyFilters.active;
        }
        if (!hasRegionStatus && availableRegionStatuses.indexOf(RegionSetStatusType.Approved) > -1) {
          hasRegionStatus = adequacyFilters.approved;
        }
        if (!hasRegionStatus && availableRegionStatuses.indexOf(RegionSetStatusType.Opportunity) > -1) {
          hasRegionStatus = adequacyFilters.opportunity;
        }
        if (!hasRegionStatus && availableRegionStatuses.indexOf(RegionSetStatusType.Pending) > -1) {
          hasRegionStatus = adequacyFilters.pending;
        }

        return hasRegionStatus;
      });
    }
  }

  if (adequacyResult.details && adequacyResult.details.length > 0 && !(specialtyGroupCode && countyRegionCode)) {
    //determine if any of the iconToggle filters for All, Gaps, and Exceptions have to be enabled or disabled depending on data relevancy to populate.
    if (adequacyResult.details.some((d) => !d.metCriteria)) {
      anyGapResults = true;
    }
    if (adequacyResult.details.some((d) => d.exception &&
      d.exception.specialtyGroupExceptionStatus !== SpecialtyGroupExceptionStatus.None &&
      d.exception.specialtyGroupExceptionStatus !== SpecialtyGroupExceptionStatus.Archived)
    ) {
      anyExceptionResults = true;
    }

    if (perspective !== ProjectPerspective.BySpecialty && perspective !== ProjectPerspective.All) {
      adequacyResult.details = adequacyResult.details.filter((v) => {
        if (adequacyFilters.active === undefined && adequacyFilters.approved === undefined && adequacyFilters.pending === undefined && adequacyFilters.opportunity === undefined) {
          return true;
        }

        if (v.regionSetId === undefined) {
          return false;
        }

        const resultRegion = {
          regionCode: v.regionCode,
          regionGranularity: v.regionGranularity,
          regionSetId: v.regionSetId,
          subRegionCode: v.subRegionCode,
        } as ResultRegion;

        const availableRegionStatuses = runConfiguration.getAvailableRegionStatusTypes(resultRegion);

        if (!(adequacyFilters.active || adequacyFilters.approved || adequacyFilters.pending) &&
          !adequacyFilters.opportunity) {
          adequacyFilters.active = adequacyFilters.approved = adequacyFilters.pending = true;
        }

        let hasRegionStatus = false;

        if (availableRegionStatuses.indexOf(RegionSetStatusType.Active) > -1 || availableRegionStatuses.indexOf(RegionSetStatusType.Unknown) > -1) {
          hasRegionStatus = adequacyFilters.active;
        }
        if (!hasRegionStatus && availableRegionStatuses.indexOf(RegionSetStatusType.Approved) > -1) {
          hasRegionStatus = adequacyFilters.approved;
        }
        if (!hasRegionStatus && availableRegionStatuses.indexOf(RegionSetStatusType.Opportunity) > -1) {
          hasRegionStatus = adequacyFilters.opportunity;
        }
        if (!hasRegionStatus && availableRegionStatuses.indexOf(RegionSetStatusType.Pending) > -1) {
          hasRegionStatus = adequacyFilters.pending;
        }

        return hasRegionStatus;
      });
    }

    if (adequacyResult.details.length > 0) {
      anyResults = true;
    }
  }

  // Coerce, if necessary, and return
  return {
    adequacyResultTypeText: adequacyResult.adequacyResultTypeText,
    aggregateDetails: adequacyResult.aggregateDetails ? adequacyResult.aggregateDetails : [],
    aggregateSummary: adequacyResult.aggregateSummary ? adequacyResult.aggregateSummary : null,
    availableRegionSetStatusTypes: summaryResultRegion ? runConfiguration.getAvailableRegionStatusTypes(summaryResultRegion) : [],
    details: adequacyResult.details ? adequacyResult.details : [],
    hasAnyResults: anyResults,
    hasExceptions: anyExceptionResults,
    hasExpandableSpecialties: anyExpandableSpecialties,
    hasGaps: anyGapResults,
  };
}

/**
 * Retrieves the updated attestation results for both current and comparison (if provided) run configurations.
 * NOTE: Wrap in try catch if necessary, this function will always allow exceptions to be thrown
 * @param runConfiguration
 * @param compareToRunConfiguration
 * @param perspective
 * @param regionFilters
 * @param adequacyAggregateFilters
 */
async function getUpdatedAttestationResults(
  runConfiguration: ProjectRunConfigurationResult,
  compareToRunConfiguration: ProjectRunConfigurationResult | undefined,
  perspective: ProjectPerspective,
  regionFilters: RegionFilters): Promise<{
    attestationResult: AttestationResult,
    compareToAttestationResult?: AttestationResult,
  }> {
  const attestationResult = await getUpdatedAttestationResultsHelper(runConfiguration, perspective, regionFilters);
  if (!attestationResult || !attestationResult.aggregateDetails || !attestationResult.details) {
    return null;
  }

  let compareToAttestationResult = await getUpdatedAttestationResultsHelper(compareToRunConfiguration, perspective, regionFilters);
  compareToAttestationResult = {
    aggregateDetails: compareToAttestationResult && compareToAttestationResult.aggregateDetails || undefined,
    aggregateSummary: compareToAttestationResult && compareToAttestationResult.aggregateSummary || undefined,
    details: compareToAttestationResult && compareToAttestationResult.details || undefined,
    resultTypeText: compareToAttestationResult && compareToAttestationResult.resultTypeText || undefined,
  };

  return {
    attestationResult,
    compareToAttestationResult,
  };
}

/**
 * Retrieves the updated composite results for both current and comparison (if provided).
 * @param runConfiguration
 * @param perspective
 * @param filters
 * @param compositeAggregateFilters
 */
async function getUpdatedCompositeResults(
  runConfiguration: ProjectRunConfigurationResult,
  compareToRunConfiguration: ProjectRunConfigurationResult | undefined,
  perspective: ProjectPerspective,
  regionFilters: RegionFilters,
  compositeFilters: CompositeFilters): Promise<{
    compositeResult: CompositeResult,
    compareToCompositeResult?: CompositeResult,
  }> {
  const compositeResult = await getUpdatedCompositeResultsHelper(runConfiguration, perspective, regionFilters, compositeFilters);
  if (!compositeResult || !compositeResult.aggregateDetails || !compositeResult.details) {
    return null;
  }

  let compareToCompositeResult = await getUpdatedCompositeResultsHelper(compareToRunConfiguration, perspective, regionFilters, compositeFilters);
  compareToCompositeResult = {
    aggregateDetails: compareToCompositeResult && compareToCompositeResult.aggregateDetails || undefined,
    aggregateSummary: compareToCompositeResult && compareToCompositeResult.aggregateSummary || undefined,
    details: compareToCompositeResult && compareToCompositeResult.details || undefined,
    resultTypeText: compareToCompositeResult && compareToCompositeResult.resultTypeText || undefined,
  };

  return {
    compareToCompositeResult,
    compositeResult,
  };
}

/**
 * All the logic for properly retrieving attestation-results for a given run-configuration
 * NOTE: Wrap in try catch if necessary, this function will always allow exceptions to be thrown
 * @param runConfiguration
 * @param perspective
 * @param filters
 */
async function getUpdatedAttestationResultsHelper(
  runConfiguration: ProjectRunConfigurationResult,
  perspective: ProjectPerspective,
  regionFilters: RegionFilters): Promise<AttestationResult> {
  const attestationResult: AttestationResult = { ...defaultAttestationResult };

  if (!runConfiguration || !perspective) {
    return attestationResult;
  }

  let countyRegionCode: string = null;
  let specialtyGroupCode: string = null;
  let stateRegionCode: string = null;

  if (regionFilters) {
    countyRegionCode = regionFilters.county;
    specialtyGroupCode = regionFilters.specialty ? decodeURIComponent(regionFilters.specialty) : regionFilters.specialty;
    stateRegionCode = regionFilters.state;
  }

  if ((perspective === ProjectPerspective.ByCounty || perspective === ProjectPerspective.ByState) && !specialtyGroupCode) {
    if (!stateRegionCode && !countyRegionCode) {
      const attestationAggregateDetails: AttestationAggregate[] = [];
      const attestationAggregateSummary: AttestationAggregate = null;

      attestationResult.aggregateDetails = attestationAggregateDetails;
      attestationResult.aggregateSummary = attestationAggregateSummary;
      attestationResult.resultTypeText = "State";
    } else if (stateRegionCode && !countyRegionCode) {
      const resultRegion = runConfiguration.getState(stateRegionCode);
      if (!resultRegion) {
        return null;
      }

      const attestationAggregateDetails: AttestationAggregate[] = [];
      const attestationAggregateSummary: AttestationAggregate = null;

      attestationResult.aggregateDetails = attestationAggregateDetails;
      attestationResult.aggregateSummary = attestationAggregateSummary;
      attestationResult.resultTypeText = "County";
    } else if (countyRegionCode) {
      const attestationAggregateSummary: AttestationAggregate = null;
      const attestationDetails: AttestationDetail[] = null;

      attestationResult.details = attestationDetails;
      attestationResult.aggregateSummary = attestationAggregateSummary;
      attestationResult.resultTypeText = "Specialty Group";
    }
  } else if (!specialtyGroupCode) {
    if (!stateRegionCode && !countyRegionCode) {
      const attestationAggregateDetails: AttestationAggregate[] = [];
      const attestationAggregateSummary: AttestationAggregate = null;

      attestationResult.aggregateDetails = attestationAggregateDetails;
      attestationResult.aggregateSummary = attestationAggregateSummary;
      attestationResult.resultTypeText = "Specialty Group";
    } else if (stateRegionCode && !countyRegionCode) {
      const attestationAggregateDetails: AttestationAggregate[] = [];
      const attestationAggregateSummary: AttestationAggregate = null;

      attestationResult.aggregateDetails = attestationAggregateDetails;
      attestationResult.aggregateSummary = attestationAggregateSummary;
      attestationResult.resultTypeText = "Specialty Group";
    } else if (countyRegionCode) {
      const attestationAggregateSummary: AttestationAggregate = null;
      const attestationDetails: AttestationDetail[] = null;

      attestationResult.details = attestationDetails;
      attestationResult.aggregateSummary = attestationAggregateSummary;
      attestationResult.resultTypeText = "Specialty Group";
    }
  } else if (specialtyGroupCode) {
    const specialtyGroup = runConfiguration.specialtyGroups.find((sg) => sg.code === specialtyGroupCode);
    if (specialtyGroup) {
      if (!stateRegionCode && !countyRegionCode) {
        const attestationAggregateDetails: AttestationAggregate[] = [];
        const attestationAggregateSummary: AttestationAggregate = null;

        attestationResult.aggregateDetails = attestationAggregateDetails;
        attestationResult.aggregateSummary = attestationAggregateSummary;
        attestationResult.resultTypeText = "State";
      } else if (stateRegionCode && !countyRegionCode) {
        const attestationAggregateSummary: AttestationAggregate = null;
        const attestationDetails: AttestationDetail[] = null;

        attestationResult.details = attestationDetails;
        attestationResult.aggregateSummary = attestationAggregateSummary;
        attestationResult.resultTypeText = "County";
      } else if (countyRegionCode) {
        const attestationAggregateSummary: AttestationAggregate = null;
        const attestationDetails: AttestationDetail[] = null;

        attestationResult.details = attestationDetails;
        attestationResult.aggregateSummary = attestationAggregateSummary;
        attestationResult.resultTypeText = "Specialty Group";
      }
    }
  }

  // Now that we have the new results, apply our filter.
  let anyResults = false;

  if (attestationResult.aggregateDetails && attestationResult.aggregateDetails.length > 0) {
    anyResults = true;
    //determine if any of the iconToggle filters for All, Gaps, and Exceptions have to be enabled or disabled depending on data relevancy to populate.
    const activeFilters: RegionSetStatusType[] = [];

    if (activeFilters.length > 0) {
      attestationResult.aggregateDetails =
        attestationResult.aggregateDetails.filter((d) => activeFilters.indexOf(d.regionStatus) > -1);
    }
  }

  if (attestationResult.details && attestationResult.details.length > 0) {
    anyResults = true;
  }

  // Coerce, if necessary, and return
  return {
    aggregateDetails: attestationResult.aggregateDetails ? attestationResult.aggregateDetails : [],
    aggregateSummary: attestationResult.aggregateSummary ? attestationResult.aggregateSummary : null,
    details: attestationResult.details ? attestationResult.details : [],
    hasAnyResults: anyResults,
    resultTypeText: attestationResult.resultTypeText,
  };
}

/**
 * Retrieves the updated Accuracy results for both current and comparison (if provided) run configurations.
 * NOTE: Wrap in try catch if necessary, this function will always allow exceptions to be thrown
 * @param runConfiguration
 * @param compareToRunConfiguration
 * @param perspective
 * @param regionFilters
 * @param accuracyFilters
 */
async function getUpdatedAccuracyResults(
  runConfiguration: ProjectRunConfigurationResult,
  compareToRunConfiguration: ProjectRunConfigurationResult | undefined,
  perspective: ProjectPerspective,
  regionFilters: RegionFilters,
  accuracyFilters: AccuracyFilters,
  accuracyService: AccuracyService,
  projectId: number,
  currentModule: string,
  useLargeProjectEnhancements: boolean
): Promise<{
  accuracyResult: AccuracyResult,
  compareToAccuracyResult?: AccuracyResult,
}> {
  const accuracyResult = await getUpdatedAccuracyResultsHelper(runConfiguration, perspective, regionFilters, accuracyFilters, accuracyService, projectId, currentModule, useLargeProjectEnhancements);
  if (!accuracyResult || !accuracyResult.aggregateDetails || !accuracyResult.details) {
    return null;
  }

  let compareToAccuracyResult = await getUpdatedAccuracyResultsHelper(compareToRunConfiguration, perspective, regionFilters, accuracyFilters, accuracyService, projectId, currentModule, useLargeProjectEnhancements);
  compareToAccuracyResult = {
    aggregateDetails: compareToAccuracyResult && compareToAccuracyResult.aggregateDetails || undefined,
    aggregateSummary: compareToAccuracyResult && compareToAccuracyResult.aggregateSummary || undefined,
    availableRegionSetStatusTypes: compareToAccuracyResult && compareToAccuracyResult.availableRegionSetStatusTypes || undefined,
    details: compareToAccuracyResult && compareToAccuracyResult.details || undefined,
    resultTypeText: compareToAccuracyResult && compareToAccuracyResult.resultTypeText || undefined,
  };

  return { accuracyResult, compareToAccuracyResult };
}

/**
 * All the logic for properly retrieving Accuracy results for a given run-configuration
 * NOTE: Wrap in try catch if necessary, this function will always allow exceptions to be thrown
 * @param runConfiguration
 * @param perspective
 * @param regionFilters
 * @param accuracyFilters
 */
async function getUpdatedAccuracyResultsHelper(
  runConfiguration: ProjectRunConfigurationResult,
  perspective: ProjectPerspective,
  regionFilters: RegionFilters,
  accuracyFilters: AccuracyFilters,
  accuracyService: AccuracyService,
  projectId: number,
  currentModule: string,
  useLargeProjectEnhancements?: boolean
): Promise<AccuracyResult> {
  const accuracyResult: AccuracyResult = { ...defaultAccuracyResult };

  if (!runConfiguration || !perspective || !runConfiguration.accuracyTemplateId || currentModule !== "Accuracy") {
    return accuracyResult;
  }

  let countyRegionCode: string = null;
  let specialtyGroupCode: string = null;
  let stateRegionCode: string = null;
  let summaryResultRegion: ResultRegion = null;

  if (regionFilters) {
    countyRegionCode = regionFilters.county;
    specialtyGroupCode = regionFilters.specialty ? decodeURIComponent(regionFilters.specialty) : regionFilters.specialty;
    stateRegionCode = regionFilters.state;
  }

  let resultRegions = runConfiguration.states();
  const parentRegions = new Map<string, StateName>();

  if (perspective === ProjectPerspective.ByCounty) {
    const regions = new Set<ResultRegion>();
    resultRegions.forEach((region) => {
      if (region.regionGranularity === RegionGranularity.County) {
        regions.add(region);
      } else if (region.regionGranularity === RegionGranularity.State) {
        region.subRegions.forEach((subRegion) => {
          if (subRegion.regionGranularity === RegionGranularity.County) {
            parentRegions.set(subRegion.regionCode, { abbreviation: region.regionCode, displayName: region.displayName });
            regions.add(subRegion);
          }
        });
      }
    });
    resultRegions = Array.from(regions);
  }

  if ((perspective === ProjectPerspective.ByCounty || perspective === ProjectPerspective.ByState) && !specialtyGroupCode) {
    if (!stateRegionCode && !countyRegionCode) {

      const accuracyAggregateDetails = (await runConfiguration.getAccuracyRegionAggregates(resultRegions, { inspectionUid: regionFilters.inspection }, useLargeProjectEnhancements).then(aggs => {
        return aggs;
      })).filter((v) => !!v);

      const accuracyAggregateSummary = await runConfiguration.getAccuracyAggregate(runConfiguration.serviceArea, { bandFilter: regionFilters.band, inspectionUid: regionFilters.inspection });

      accuracyAggregateDetails.forEach((detail) => {
        if (parentRegions.has(detail.regionCode)) {
          const parentInfo = parentRegions.get(detail.regionCode);
          detail.parentAbbreviation = parentInfo.abbreviation;
          detail.parentDisplayName = parentInfo.displayName;
        }
      });

      summaryResultRegion = runConfiguration.serviceArea;

      accuracyResult.aggregateDetails = accuracyAggregateDetails;
      accuracyResult.aggregateSummary = accuracyAggregateSummary;
      accuracyResult.resultTypeText = "State";
      // at the top level, if opportunity is not selected, disregard previous selections for active, pending, and approved; otherwise, include all service area statuses
      accuracyFilters.active = accuracyFilters.pending = accuracyFilters.approved = (!accuracyFilters.opportunity ? undefined : true);
    } else if (stateRegionCode && !countyRegionCode) {
      const resultRegion = runConfiguration.getState(stateRegionCode);
      if (!resultRegion) {
        return null;
      }

      summaryResultRegion = resultRegion;
      const accuracyAggregateDetails = (await runConfiguration.getAccuracyRegionAggregates(resultRegion.subRegions, { inspectionUid: regionFilters.inspection }, useLargeProjectEnhancements).then(aggs => {
        return aggs;
      })).filter((v) => {
        if (!v) {
          return false;
        }

        if (accuracyFilters.active === undefined && accuracyFilters.approved === undefined && accuracyFilters.pending === undefined && accuracyFilters.opportunity === undefined) {
          return true;
        }
        if (!(accuracyFilters.active || accuracyFilters.approved || accuracyFilters.pending) &&
          !accuracyFilters.opportunity) {
          accuracyFilters.active = accuracyFilters.approved = accuracyFilters.pending = true;
        }
        if (!v.regionStatus ||
          v.regionStatus === RegionSetStatusType.Active ||
          v.regionStatus === RegionSetStatusType.Unknown) {
          return (accuracyFilters.active);
        } else if (v.regionStatus === RegionSetStatusType.Approved) {
          return (accuracyFilters.approved);
        } else if (v.regionStatus === RegionSetStatusType.Opportunity) {
          return (accuracyFilters.opportunity);
        } else {
          return (accuracyFilters.pending);
        }
      });

      accuracyAggregateDetails.forEach((detail) => {
        if (parentRegions.has(detail.regionCode)) {
          const parentInfo = parentRegions.get(detail.regionCode);
          detail.parentAbbreviation = parentInfo.abbreviation;
          detail.parentDisplayName = parentInfo.displayName;
        }
      });

      const accuracyAggregateSummary = await runConfiguration.getAccuracyAggregate(resultRegion, { bandFilter: regionFilters.band, inspectionUid: regionFilters.inspection });
      accuracyResult.aggregateDetails = accuracyAggregateDetails;
      accuracyResult.aggregateSummary = accuracyAggregateSummary;
      accuracyResult.resultTypeText = "County";
    } else if (countyRegionCode) {
      const resultRegion = runConfiguration.getCounty(countyRegionCode);
      summaryResultRegion = resultRegion;
      const accuracyAggregateSummary = await runConfiguration.getAccuracyAggregate(resultRegion, { bandFilter: regionFilters.band, inspectionUid: regionFilters.inspection });
      const accuracyDetails = await runConfiguration.getAccuracyDetails(resultRegion, undefined, "specialty");

      accuracyResult.details = accuracyDetails;
      accuracyResult.aggregateSummary = accuracyAggregateSummary;
      accuracyResult.resultTypeText = "Specialty Group";
    }
  } else if (perspective === ProjectPerspective.BySpecialty && !specialtyGroupCode) {
    if (!stateRegionCode && !countyRegionCode) {
      const resultRegion = runConfiguration.serviceArea;
      summaryResultRegion = resultRegion;

      const specialtyGroupAggregates = await accuracyService.specialtyGroupResults(
        projectId,
        resultRegion.regionSetId,
        runConfiguration.projectRunConfigurationId,
        runConfiguration.networkAssessmentAnalysisType,
      );

      const promises = runConfiguration.specialtyGroups.map<Promise<AccuracyAggregate>>(async (sg) => {
        return await runConfiguration.getAccuracyAggregate(resultRegion, { aggregateDisplayType: "specialty", specialtyGroup: sg, specialtyGroupAggregates: specialtyGroupAggregates });
      });

      const accuracyAggregateDetails = (await Promise.all(promises)).filter((v) => !!v);
      const accuracyAggregateSummary = await runConfiguration.getAccuracyAggregate(resultRegion);

      accuracyResult.aggregateDetails = accuracyAggregateDetails;
      accuracyResult.aggregateSummary = accuracyAggregateSummary;
      accuracyResult.resultTypeText = "Specialty Group";
    } else if (stateRegionCode && !countyRegionCode) {
      const resultRegion = runConfiguration.getState(stateRegionCode);
      summaryResultRegion = resultRegion;

      const specialtyGroupAggregates = await accuracyService.specialtyGroupResults(
        projectId,
        resultRegion.regionSetId,
        runConfiguration.projectRunConfigurationId,
        runConfiguration.networkAssessmentAnalysisType,
      );

      const promises = runConfiguration.specialtyGroups.map<Promise<AccuracyAggregate>>(async (sg) => {
        return await runConfiguration.getAccuracyAggregate(resultRegion, { aggregateDisplayType: "specialty", specialtyGroup: sg, specialtyGroupAggregates: specialtyGroupAggregates });
      });

      const accuracyAggregateDetails = (await Promise.all(promises)).filter((v) => !!v);
      const accuracyAggregateSummary = await runConfiguration.getAccuracyAggregate(resultRegion);

      accuracyResult.aggregateDetails = accuracyAggregateDetails;
      accuracyResult.aggregateSummary = accuracyAggregateSummary;
      accuracyResult.resultTypeText = "Specialty Group";
    } else if (countyRegionCode) {
      const resultRegion = runConfiguration.getCounty(countyRegionCode);
      summaryResultRegion = resultRegion;
      const accuracyAggregateSummary = await runConfiguration.getAccuracyAggregate(resultRegion);
      const accuracyDetails = await runConfiguration.getAccuracyDetails(resultRegion, undefined, "specialty");

      accuracyResult.details = accuracyDetails;
      accuracyResult.aggregateSummary = accuracyAggregateSummary;
      accuracyResult.resultTypeText = "Specialty Group";
    }
  } else if (perspective !== ProjectPerspective.All && specialtyGroupCode) {
    const specialtyGroup = runConfiguration.specialtyGroups.find((sg) => sg.code === specialtyGroupCode);
    if (specialtyGroup) {
      if (!stateRegionCode && !countyRegionCode) {
        let resultRegions: ResultRegion[] = [];
        const states = runConfiguration.states();
        if (perspective === ProjectPerspective.ByState) {
          resultRegions = states;
          accuracyResult.resultTypeText = "State";
        } else if (perspective === ProjectPerspective.ByCounty) {
          // prevent runConfiguration.counties() from returning duplicates
          resultRegions = runConfiguration.counties().filter((county, index, self) => {
            return index === self.findIndex((c) => c.regionCode === county.regionCode);
          });

          accuracyResult.resultTypeText = "County";
        }

        const accuracyAggregates = await runConfiguration.getBulkRegionSetSpecialtyGroupAccuracyAggregates(resultRegions, specialtyGroup);

        let accuracyAggregateDetails: AccuracyAggregate[] = [];
        if (accuracyAggregates) {
          accuracyAggregateDetails = accuracyAggregates.filter((v) => !!v);
        }

        const specialtyGroupAggregates = await accuracyService.specialtyGroupResults(
          projectId,
          runConfiguration.serviceArea.regionSetId,
          runConfiguration.projectRunConfigurationId,
          runConfiguration.networkAssessmentAnalysisType,
        );

        const accuracyAggregateSummary = await runConfiguration.getAccuracyAggregate(runConfiguration.serviceArea, { specialtyGroup: specialtyGroup, specialtyGroupAggregates: specialtyGroupAggregates });

        if (perspective === ProjectPerspective.ByCounty) {
          accuracyAggregateDetails.forEach((detail) => {
            const regionCode = detail.regionCode;
            const countyState = states.find((state) => !!state.subRegions.find((sr: ResultRegion) => sr.regionCode === regionCode));
            detail.parentAbbreviation = countyState.regionCode;
            detail.parentDisplayName = countyState.displayName;
          });
        }

        summaryResultRegion = runConfiguration.serviceArea;
        accuracyResult.aggregateDetails = accuracyAggregateDetails;
        accuracyResult.aggregateSummary = accuracyAggregateSummary;
      } else if (stateRegionCode && !countyRegionCode) {
        const resultRegion = runConfiguration.getState(stateRegionCode);
        summaryResultRegion = resultRegion;

        const specialtyGroupAggregates = await accuracyService.specialtyGroupResults(
          projectId,
          resultRegion.regionSetId,
          runConfiguration.projectRunConfigurationId,
          runConfiguration.networkAssessmentAnalysisType,
        );

        const accuracyAggregateSummary = await runConfiguration.getAccuracyAggregate(resultRegion, { specialtyGroup: specialtyGroup, specialtyGroupAggregates: specialtyGroupAggregates });

        const accuracyDetails = await runConfiguration.getAccuracyDetails(resultRegion, specialtyGroup);

        accuracyResult.details = accuracyDetails;
        accuracyResult.aggregateSummary = accuracyAggregateSummary;
        accuracyResult.resultTypeText = "County";
      } else if (countyRegionCode) {
        const resultRegion = runConfiguration.getCounty(countyRegionCode);
        summaryResultRegion = resultRegion;
        const accuracyAggregateSummary = await runConfiguration.getAccuracyAggregate(resultRegion, { bandFilter: regionFilters.band, inspectionUid: regionFilters.inspection, specialtyGroup: specialtyGroup });
        const accuracyDetails = await runConfiguration.getAccuracyDetails(resultRegion, specialtyGroup);

        accuracyResult.details = accuracyDetails;
        accuracyResult.aggregateSummary = accuracyAggregateSummary;
        accuracyResult.resultTypeText = "Specialty Group";
      }
    } else {
      summaryResultRegion = runConfiguration.serviceArea;
      accuracyResult.aggregateDetails = [];
      accuracyResult.aggregateSummary = await runConfiguration.getAccuracyAggregate(runConfiguration.serviceArea);
      accuracyResult.details = [];
      accuracyResult.resultTypeText = "State";
    }
  } else if (perspective === ProjectPerspective.All) {
    let resultRegions = runConfiguration.states();
    let specialtyGroups = runConfiguration.specialtyGroups;
    if (stateRegionCode) {
      resultRegions = [runConfiguration.getState(stateRegionCode)];
    }
    if (countyRegionCode) {
      resultRegions = [runConfiguration.getCounty(countyRegionCode)];
    }
    if (specialtyGroupCode) {
      specialtyGroups = [runConfiguration.specialtyGroups.find((sg) => sg.code === specialtyGroupCode)];
    }

    // Track the parents of regions so we can print state names in the table
    const parentRegions = new Map<string, StateName>();

    const regions = new Set<ResultRegion>();
    if (resultRegions.some((item) => !!item)) {
      // We decided we always want the county-level granularity
      resultRegions.forEach((region) => {
        if (region.regionGranularity === RegionGranularity.County) {
          regions.add(region);
        } else if (region.regionGranularity === RegionGranularity.State) {
          region.subRegions.forEach((subRegion) => {
            if (subRegion.regionGranularity === RegionGranularity.County) {
              parentRegions.set(subRegion.regionCode, { abbreviation: region.regionCode, displayName: region.displayName });
              regions.add(subRegion);
            }
          });
        }
      });
      resultRegions = Array.from(regions);
    }

    // Make a list of all the details we need for each region
    let detailPromises = new Array<Promise<AccuracyDetail[]>>();

    const regionArray = Array.from(regions);
    if (specialtyGroups.length === 1 && regionArray.length === 1) {
      //filtered by county and specialty group, just need to retrieve the single result
      detailPromises = resultRegions.length && specialtyGroups.length ? Array.from(resultRegions).map(async (region) => await runConfiguration.getAccuracyDetails(region, specialtyGroups.length === 1 ? specialtyGroups[0] : null)) : [];
    }
    // TO-DO: Populate accuracy details correctly with proper batches/skips/takes, if it's even possible?
    //else {
    //  const batchedRegions: ResultRegion[][] = [];
    //  const regionCount = regionArray.length;
    //  const batchSize = 50; //arbitrary
    //  for (let i = 0; i < regionCount; i += batchSize) {
    //    batchedRegions.push(regionArray.slice(i, i + batchSize));
    //  }

    //  detailPromises = resultRegions.length && specialtyGroups.length ? batchedRegions.map(async (regionBatch) => await runConfiguration.getBatchedAccuracyDetails(regionBatch)) : [];
    //}

    // Get the details. When the data is returned from the Promises, condense all of it into a single array and return it
    const accuracyDetails = await Promise.all(detailPromises).then((data) => {
      const condensedData = data.reduce((previousValue, currentValue) => {
        (currentValue || []).forEach((value) => previousValue.push(value));
        return previousValue;
      }, []);
      return condensedData;
    });

    accuracyDetails.forEach((detail) => {
      if (parentRegions.has(detail.regionCode)) {
        const parentInfo = parentRegions.get(detail.regionCode);
        detail.parentAbbreviation = parentInfo.abbreviation;
        detail.parentDisplayName = parentInfo.displayName;
      }
    });

    let specialtyGroupAggregates = undefined;
    if (specialtyGroups.length === 1) {
      specialtyGroupAggregates = await accuracyService.specialtyGroupResults(
        projectId,
        resultRegions.length === 1 ? resultRegions[0].regionSetId : runConfiguration.serviceArea.regionSetId,
        runConfiguration.projectRunConfigurationId,
        runConfiguration.networkAssessmentAnalysisType,
      );
    }

    const accuracyAggregateSummary = await runConfiguration.getAccuracyAggregate(resultRegions.length === 1 ? resultRegions[0] : runConfiguration.serviceArea,
      { specialtyGroup: specialtyGroups.length === 1 ? specialtyGroups[0] : null, specialtyGroupAggregates: specialtyGroupAggregates });

    summaryResultRegion = runConfiguration.serviceArea;
    accuracyResult.details = accuracyDetails;
    accuracyResult.aggregateSummary = accuracyAggregateSummary;
    accuracyResult.resultTypeText = "Specialty Group";
    // at the top level, if opportunity is not selected, disregard previous selections for active, pending, and approved; otherwise, include all service area statuses
    accuracyFilters.active = accuracyFilters.pending = accuracyFilters.approved = (!accuracyFilters.opportunity ? undefined : true);
  }

  // Now that we have the new results, apply our filter.
  let anyResults = false;

  if (accuracyResult.aggregateDetails && accuracyResult.aggregateDetails.length > 0) {
    const activeFilters: RegionSetStatusType[] = [RegionSetStatusType.Unknown];

    if (!(accuracyFilters.active || accuracyFilters.approved || accuracyFilters.pending) &&
      !accuracyFilters.opportunity) {
      accuracyFilters.active = accuracyFilters.approved = accuracyFilters.pending = true;
    }

    if (accuracyFilters.active) {
      activeFilters.push(RegionSetStatusType.Active);
    }
    if (accuracyFilters.approved) {
      activeFilters.push(RegionSetStatusType.Approved);
    }
    if (accuracyFilters.pending) {
      activeFilters.push(RegionSetStatusType.Pending);
    }
    if (accuracyFilters.opportunity) {
      activeFilters.push(RegionSetStatusType.Opportunity);
    }

    if (activeFilters.length > 0) {
      accuracyResult.aggregateDetails =
        accuracyResult.aggregateDetails.filter((d) => activeFilters.indexOf(d.regionStatus) > -1);
    }
    if (accuracyResult.aggregateDetails.length > 0) {
      anyResults = true;
    }
  }

  if (accuracyResult.details && accuracyResult.details.length > 0) {
    if (accuracyResult.details.length > 0) {
      anyResults = true;
    }
  }

  // Coerce, if necessary, and return
  return {
    aggregateDetails: accuracyResult.aggregateDetails ? accuracyResult.aggregateDetails : [],
    aggregateSummary: accuracyResult.aggregateSummary ? accuracyResult.aggregateSummary : null,
    availableRegionSetStatusTypes: summaryResultRegion ? runConfiguration.getAvailableRegionStatusTypes(summaryResultRegion) : [],
    details: accuracyResult.details ? accuracyResult.details : [],
    hasAnyResults: anyResults,
    resultTypeText: accuracyResult.resultTypeText,
  };
}

/**
 * All the logic for properly retrieving composite-results for a given run-configuration
 * NOTE: Wrap in try catch if necessary, this function will always allow exceptions to be thrown
 * @param runConfiguration
 * @param perspective
 * @param filters
 * @param compositeFilters
 */
async function getUpdatedCompositeResultsHelper(
  runConfiguration: ProjectRunConfigurationResult,
  perspective: ProjectPerspective,
  regionFilters: RegionFilters,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  compositeFilters: CompositeFilters): Promise<CompositeResult> {
  const compositeResult: CompositeResult = { ...defaultCompositeResult };
  // disabling composite functionality
  return compositeResult;

  if (!runConfiguration || !perspective) {
    return compositeResult;
  }

  let countyRegionCode: string = null;
  let specialtyGroupCode: string = null;
  let stateRegionCode: string = null;

  if (regionFilters) {
    countyRegionCode = regionFilters.county;
    specialtyGroupCode = regionFilters.specialty ? decodeURIComponent(regionFilters.specialty) : regionFilters.specialty;
    stateRegionCode = regionFilters.state;
  }

  if ((perspective === ProjectPerspective.ByCounty || perspective === ProjectPerspective.ByState) && !specialtyGroupCode) {
    if (!stateRegionCode && !countyRegionCode) {
      const resultRegions = runConfiguration.states();

      const promises = resultRegions.map<Promise<CompositeAggregate>>(async (rr) => {
        return await runConfiguration.getCompositeAggregate(rr);
      });

      const compositeAggregateDetails = (await Promise.all(promises)).filter((v) => !!v);
      const compositeAggregateSummary = await runConfiguration.getCompositeAggregate(runConfiguration.serviceArea);

      compositeResult.aggregateDetails = compositeAggregateDetails;
      compositeResult.aggregateSummary = compositeAggregateSummary;
      compositeResult.resultTypeText = "State";
    } else if (stateRegionCode && !countyRegionCode) {
      const resultRegion = runConfiguration.getState(stateRegionCode);

      const promises = resultRegion.subRegions.map<Promise<CompositeAggregate>>(async (rr) => {
        return await runConfiguration.getCompositeAggregate(rr);
      });

      const compositeAggregateDetails = (await Promise.all(promises)).filter((v) => !!v);
      const compositeAggregateSummary = await runConfiguration.getCompositeAggregate(resultRegion);

      compositeResult.aggregateDetails = compositeAggregateDetails;
      compositeResult.aggregateSummary = compositeAggregateSummary;
      compositeResult.resultTypeText = "County";
    } else if (countyRegionCode) {
      const resultRegion = runConfiguration.getCounty(countyRegionCode);
      const compositeAggregateSummary = await runConfiguration.getCompositeAggregate(resultRegion);
      const compositeDetails = await runConfiguration.getCompositeDetails(resultRegion, undefined, "specialty");

      compositeResult.details = compositeDetails;
      compositeResult.aggregateSummary = compositeAggregateSummary;
      compositeResult.resultTypeText = "Specialty";
    }
  } else if (!specialtyGroupCode) {
    if (!stateRegionCode && !countyRegionCode) {
      const resultRegion = runConfiguration.serviceArea;

      const promises = runConfiguration.specialtyGroups.map<Promise<CompositeAggregate>>(async (sg) => {
        return await runConfiguration.getCompositeAggregate(resultRegion, sg, "specialty");
      });

      const compositeAggregateDetails = (await Promise.all(promises)).filter((v) => !!v);
      const compositeAggregateSummary = await runConfiguration.getCompositeAggregate(resultRegion);

      compositeResult.aggregateDetails = compositeAggregateDetails;
      compositeResult.aggregateSummary = compositeAggregateSummary;
      compositeResult.resultTypeText = "Specialty";
    } else if (stateRegionCode && !countyRegionCode) {
      const resultRegion = runConfiguration.getState(stateRegionCode);

      const promises = runConfiguration.specialtyGroups.map<Promise<CompositeAggregate>>(async (sg) => {
        return await runConfiguration.getCompositeAggregate(resultRegion, sg, "specialty");
      });

      const compositeAggregateDetails = (await Promise.all(promises)).filter((v) => !!v);
      const compositeAggregateSummary = await runConfiguration.getCompositeAggregate(resultRegion);

      compositeResult.aggregateDetails = compositeAggregateDetails;
      compositeResult.aggregateSummary = compositeAggregateSummary;
      compositeResult.resultTypeText = "Specialty";
    } else if (countyRegionCode) {
      const resultRegion = runConfiguration.getCounty(countyRegionCode);
      const compositeAggregateSummary = await runConfiguration.getCompositeAggregate(resultRegion);
      const compositeDetails = await runConfiguration.getCompositeDetails(resultRegion, undefined, "specialty");

      compositeResult.details = compositeDetails;
      compositeResult.aggregateSummary = compositeAggregateSummary;
      compositeResult.resultTypeText = "Specialty";
    }
  } else if (specialtyGroupCode) {
    const specialtyGroup = runConfiguration.specialtyGroups.find((sg) => sg.code === specialtyGroupCode);
    if (specialtyGroup) {
      if (!stateRegionCode && !countyRegionCode) {
        const resultRegions = runConfiguration.states();
        const promises = resultRegions.map<Promise<CompositeAggregate>>(async (rr) => {
          return await runConfiguration.getCompositeAggregate(rr, specialtyGroup);
        });

        const compositeAggregateDetails = (await Promise.all(promises)).filter((v) => !!v);
        const compositeAggregateSummary = await runConfiguration.getCompositeAggregate(runConfiguration.serviceArea, specialtyGroup);

        compositeResult.aggregateDetails = compositeAggregateDetails;
        compositeResult.aggregateSummary = compositeAggregateSummary;
        compositeResult.resultTypeText = "State";
      } else if (stateRegionCode && !countyRegionCode) {
        const resultRegion = runConfiguration.getState(stateRegionCode);
        const compositeAggregateSummary = await runConfiguration.getCompositeAggregate(resultRegion, specialtyGroup);
        const compositeDetails = await runConfiguration.getCompositeDetails(resultRegion, specialtyGroup);

        compositeResult.details = compositeDetails;
        compositeResult.aggregateSummary = compositeAggregateSummary;
        compositeResult.resultTypeText = "County";
      } else if (countyRegionCode) {
        const resultRegion = runConfiguration.getCounty(countyRegionCode);
        const compositeAggregateSummary = await runConfiguration.getCompositeAggregate(resultRegion, specialtyGroup);
        const compositeDetails = await runConfiguration.getCompositeDetails(resultRegion, specialtyGroup);

        compositeResult.details = compositeDetails;
        compositeResult.aggregateSummary = compositeAggregateSummary;
        compositeResult.resultTypeText = "Specialty";
      }
    }
  }

  // Now that we have the new results, apply our filter.
  let anyResults = false;

  if (compositeResult.aggregateDetails && compositeResult.aggregateDetails.length > 0) {
    anyResults = true;
    //determine if any of the iconToggle filters for All, Gaps, and Exceptions have to be enabled or disabled depending on data relevancy to populate.
    const activeFilters: RegionSetStatusType[] = [];

    if (activeFilters.length > 0) {
      compositeResult.aggregateDetails =
        compositeResult.aggregateDetails.filter((d) => activeFilters.indexOf(d.regionStatus) > -1);
    }
  }

  if (compositeResult.details && compositeResult.details.length > 0) {
    anyResults = true;
  }

  // Coerce, if necessary, and return
  return {
    aggregateDetails: compositeResult.aggregateDetails ? compositeResult.aggregateDetails : [],
    aggregateSummary: compositeResult.aggregateSummary ? compositeResult.aggregateSummary : null,
    details: compositeResult.details ? compositeResult.details : [],
    hasAnyResults: anyResults,
    resultTypeText: compositeResult.resultTypeText,
  };
}

/**
 * Generates a key for the provided run-configuration-id & adequacy-analysis-type
 * @param runConfigurationId
 * @param networkAssessmentAnalysisType
 */
function keyFor(runConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): string {
  return `${runConfigurationId}:${networkAssessmentAnalysisType}`;
}

/**
 * Parses a set of filters from the provided url path.
 * Returns the values of said filters.
 */
function parseRegionFilters(pathname: string): RegionFilters {
  const filters = pathname.substr(1).split("/");

  const keyedFilters: { [key: string]: string } = {};

  filters.forEach((s, i) => {
    if (i > 2) {
      if (i % 2 === 0) {
        keyedFilters[filters[i - 1]] = s;
      } else {
        keyedFilters[s] = "";
      }
    }
  });

  return keyedFilters;
}
